// src/pages/nafed/pmc/NafedPmcDashboard.js

import { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db, storage } from "../../../firebaseConfig";
import toast, { Toaster } from "react-hot-toast";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

import PacsApplicationsPane from "../../pmc/PmcPacsApplicationsPane";
import FieldVisitPane from "../../pmc/PmcFieldVisitPane";
import ApprovalDeskPane from "../../pmc/PmcApprovalDeskPane";
import PmcBoqSetupPane from "../../pmc/PmcBoqSetupPane";
import PmcDailyProgressReport from "../../pmc/PmcDailyProgressReport";
import PmcEngineerMeasurementSetupPane from "../../pmc/PmcEngineerMeasurementSetupPane";
import PmcLoaWorkOrderPane from "../../pmc/PmcLoaWorkOrderPane";
import PmcContractorDocumentsPane from "../../pmc/PmcContractorDocumentsPane";
import GenerateLOA from "../../pmc/GenerateLOA";
import PmcPacsDocumentScrutiny from "./PmcPacsDocumentScrutiny";
import PmcMouSigning from "./PmcMouSigning";
import { getMouSigningState, getCurrentFinalMouUrl } from "../pacs/PacsMouSigning";
import NafedPmcWorkflowPanel from "./NafedPmcWorkflowPanel";
import NafedDprGenerator from "./NafedDprGenerator";
import {
  getNafedAssuranceLetter,
  isMouExecuted as isOfficialMouExecuted,
} from "./nafedWorkflowBridge";
import { NAFED_COLLECTIONS } from "./nafedCollections";

const MODULES = [
  {
    key: "registrationApproval",
    title: "PACS Registration & Access",
    desc: "Verify allocated PACS registration and authorise portal access.",
    icon: "ID",
    color: "orange",
  },
  {
    key: "pacs",
    title: "Allocated PACS Applications",
    desc: "Review every application allocated to your PMC.",
    icon: "▤",
    color: "green",
  },
  {
    key: "visit",
    title: "Site Visit & Geo Verification",
    desc: "Plan visits and verify site photo, GPS and field evidence.",
    icon: "⌖",
    color: "orange",
  },
  {
    key: "pmcapproval",
    title: "Application Decision Desk",
    desc: "Approve, return or reject PACS applications with an audit trail.",
    icon: "✓",
    color: "green",
  },
  {
    key: "pacsDocumentScrutiny",
    title: "PACS Document Verification",
    desc: "Verify signed PACS documents, GPS evidence and unlock MOU.",
    icon: "DOC",
    color: "purple",
  },
  {
    key: "mouExecution",
    title: "PMC–PACS MOU Execution",
    desc: "PMC signs first and PACS completes the two-party MOU.",
    icon: "MOU",
    color: "teal",
  },
  {
    key: "technicalVendors",
    title: "Technical Work Allocation",
    desc: "Allocate survey, soil, design, estimate and DPR work in a controlled sequence.",
    icon: "TV",
    color: "blue",
  },
  {
    key: "dprGenerator",
    title: "DPR Upload & Publication",
    desc: "Publish the complete PMC-prepared and signed DPR to PACS.",
    icon: "DPR",
    color: "green",
  },
  {
    key: "assuranceLetter",
    title: "NAFED Assurance Letter Desk",
    desc: "Upload the official NAFED-issued letter and publish it directly to PACS.",
    icon: "NAL",
    color: "teal",
  },
  {
    key: "registrarForwarding",
    title: "Registrar File Forwarding & Compliance",
    desc: "Forward the complete PACS package to District and answer District or State queries.",
    icon: "REG",
    color: "orange",
  },
  {
    key: "nafedTender",
    title: "NAFED Tender Publication",
    desc: "Publish eligible projects to the All India Tender Portal.",
    icon: "TN",
    color: "yellow",
  },
  {
    key: "boq",
    title: "BOQ Management",
    desc: "BOQ, quantity and cost setup.",
    icon: "▥",
    color: "blue",
  },
  {
    key: "measSetup",
    title: "Measurement Book",
    desc: "Measurement template setup.",
    icon: "▧",
    color: "purple",
  },
  {
    key: "dailyProgress",
    title: "Daily Progress",
    desc: "Review site daily progress.",
    icon: "▨",
    color: "blue",
  },
  {
    key: "generateLoa",
    title: "Generate LOA",
    desc: "Generate LOA document.",
    icon: "◫",
    color: "yellow",
  },
  {
    key: "loa",
    title: "LOA / Work Order",
    desc: "LOA and work order tracking.",
    icon: "▣",
    color: "teal",
  },
  {
    key: "contractorDocs",
    title: "Contractor Documents",
    desc: "Verify uploaded documents.",
    icon: "▰",
    color: "pink",
  },

  // Future modules add here
  // { key: "reports", title: "MIS Reports", desc: "Project MIS and analytics.", icon: "▦", color: "blue" },
];

const clean = (v = "") =>
  String(v || "")
    .trim()
    .toLowerCase();

function isStrictNafedApplyProject(p = {}) {
  return (
    p.nafedProject === true &&
    clean(p.projectSource) === "nafed" &&
    clean(p.applicationPortal) === "nafed_pacs_apply"
  );
}

const REMOVED_STATUSES = new Set([
  "DELETED",
  "ARCHIVED",
  "REMOVED",
  "TRASHED",
  "SOFT_DELETED",
  "PURGED",
  "CANCELLED",
]);

function isDeletedRecord(item = {}) {
  return (
    item.isDeleted === true ||
    item.deleted === true ||
    item.isArchived === true ||
    item.archived === true ||
    Boolean(
      item.deletedAt ||
        item.archivedAt ||
        item.removedAt ||
        item.purgedAt,
    ) ||
    [
      item.status,
      item.registrationStatus,
      item.applicationStatus,
      item.recordStatus,
      item.pacsStatus,
      item.allocationStatus,
    ].some((value) => REMOVED_STATUSES.has(String(value || "").trim().toUpperCase()))
  );
}

function getAllocatedPmcUid(item = {}) {
  return String(
    item.allocatedPmcUid ||
      item.assignedPmcUid ||
      item.pmcUid ||
      item.allocatedPmcId ||
      item.assignedPmcId ||
      item.pmcId ||
      "",
  ).trim();
}

function getDistrict(p = {}) {
  return (
    p.district || p.districtName || p.projectDistrict || p.pacsDistrict || ""
  );
}

function getState(p = {}) {
  return p.state || p.stateName || p.projectState || p.pacsState || "";
}

function getCapacity(p = {}) {
  const sources = [
    p,
    p.projectDetails,
    p.applicationData,
    p.formData,
    p.godownDetails,
    p.technicalDetails,
    p.project,
  ].filter(Boolean);
  const fields = [
    "proposedCapacityMt",
    "proposedCapacityMT",
    "capacityMt",
    "capacityMT",
    "storageCapacityMt",
    "storageCapacityMT",
    "projectCapacityMt",
    "projectCapacityMT",
    "approvedCapacityMt",
    "approvedCapacityMT",
    "sanctionedCapacityMt",
    "sanctionedCapacityMT",
    "godownCapacityMt",
    "godownCapacityMT",
    "storageCapacity",
    "godownCapacity",
    "selectedCapacity",
    "capacityValue",
    "capacity",
  ];

  for (const source of sources) {
    for (const field of fields) {
      const raw = source?.[field];
      const candidate =
        raw && typeof raw === "object"
          ? raw.value ?? raw.mt ?? raw.capacity ?? raw.label
          : raw;
      if (candidate === undefined || candidate === null || candidate === "") {
        continue;
      }
      const value = Number(
        String(candidate)
          .replace(/,/g, "")
          .replace(/[^0-9.-]/g, "")
          .trim(),
      );
      if (Number.isFinite(value) && value > 0) return value;
    }
  }
  return 0;
}

function getPacsName(p = {}) {
  return (
    p.pacsName ||
    p.societyName ||
    p.applicantName ||
    p.organizationName ||
    p.pacsRegistrationNo ||
    "-"
  );
}

function formatCapacity(mt) {
  const n = Number(mt || 0);
  if (n >= 100000) return `${(n / 100000).toFixed(2)} LMT`;
  return `${n.toLocaleString("en-IN")} MT`;
}

function getPacsKeys(item = {}, includeName = true) {
  const values = [
    item.id,
    item.uid,
    item.pacsUid,
    item.pacsId,
    item.pacsUserId,
    item.pacsUserUid,
    item.pacsRegistrationId,
    item.registrationId,
    item.societyId,
    item.userId,
    item.applicantUid,
    item.email,
    item.pacsEmail,
    item.registrationNumber,
    item.pacsRegistrationNo,
  ];

  if (includeName) {
    values.push(
      item.pacsName,
      item.societyName,
      item.organizationName,
      item.applicantName,
      item.name,
    );
  }

  return [...new Set(values.filter(Boolean).map(clean).filter(Boolean))];
}

function isPacsRole(item = {}) {
  return ["PACS", "PACS_SOCIETY", "PACS / SOCIETY"].includes(
    String(item.role || "").trim().toUpperCase(),
  );
}

function isActiveAllocation(item = {}) {
  const status = String(
    item.status || item.allocationStatus || "ACTIVE",
  ).trim().toUpperCase();
  return (
    !isDeletedRecord(item) &&
    item.isActive !== false &&
    !["UNALLOCATED", "REVOKED", "INACTIVE", "EXPIRED"].includes(status)
  );
}

function matchesAnyKey(item = {}, allowedKeys = new Set()) {
  return getPacsKeys(item).some((key) => allowedKeys.has(key));
}

function LoaderScreen() {
  return (
    <>
      <style>{`
        body{margin:0;font-family:Inter,Segoe UI,Arial,sans-serif;background:#061b2f}
        .loader-page{
          min-height:100vh;display:grid;place-items:center;color:white;overflow:hidden;
          background:
            radial-gradient(circle at 10% 10%,rgba(34,197,94,.22),transparent 30%),
            radial-gradient(circle at 90% 18%,rgba(14,165,233,.20),transparent 28%),
            linear-gradient(135deg,#041329 0%,#062b55 45%,#064e3b 100%);
          position:relative;
        }
        .loader-page:before{
          content:"";position:fixed;inset:0;
          background-image:
            linear-gradient(rgba(255,255,255,.055) 1px,transparent 1px),
            linear-gradient(90deg,rgba(255,255,255,.055) 1px,transparent 1px);
          background-size:52px 52px;opacity:.8;
        }
        .loader-card{
          position:relative;z-index:2;width:min(460px,calc(100% - 36px));
          border-radius:28px;background:rgba(255,255,255,.12);
          border:1px solid rgba(255,255,255,.18);
          box-shadow:0 30px 90px rgba(0,0,0,.35);
          backdrop-filter:blur(18px);padding:34px;text-align:center;
        }
        .loader-logo{
          width:155px;height:82px;border-radius:18px;margin:0 auto 20px;
          background:#fff;display:flex;align-items:center;justify-content:center;
          padding:8px;box-shadow:0 10px 25px rgba(0,0,0,.18);
        }
        .loader-logo img{width:100%;height:100%;object-fit:contain;display:block}
        .loader-title{font-size:26px;font-weight:950;margin:0}
        .loader-sub{margin:8px 0 0;color:#d1fae5;font-size:13px;font-weight:800}
        .loader-bar{margin-top:24px;height:9px;border-radius:999px;background:rgba(255,255,255,.16);overflow:hidden}
        .loader-bar span{
          display:block;width:42%;height:100%;border-radius:999px;
          background:linear-gradient(90deg,#22c55e,#38bdf8);
          animation:loaderMove 1.2s ease-in-out infinite;
        }
        @keyframes loaderMove{
          0%{transform:translateX(-110%)}
          100%{transform:translateX(260%)}
        }
      `}</style>

      <div className="loader-page">
        <div className="loader-card">
          <div className="loader-logo">
            <img src="/images/nafed-logo.png" alt="NAFED" />
          </div>
          <h1 className="loader-title">PMC Portal</h1>
          <p className="loader-sub">
            Securing allocated PACS project workspace...
          </p>
          <div className="loader-bar">
            <span />
          </div>
        </div>
      </div>
    </>
  );
}

function PacsRegistrationApprovalPane({
  records,
  onApprove,
  approvingPacsId = "",
}) {
  const [filter, setFilter] = useState("PENDING");
  const visible = records.filter((item) => {
    const approved =
      item.status === "APPROVED" &&
      item.isActive === true &&
      item.workStartApproved === true;
    return filter === "ALL" || (filter === "APPROVED" ? approved : !approved);
  });

  return (
    <section className="registration-desk">
      <div className="registration-head">
        <div>
          <small>PRE-APPLICATION ACCESS CONTROL</small>
          <h2>PACS Registration and Work Start Approval</h2>
          <p>
            Verify the registered PACS before enabling portal access and
            application submission.
          </p>
        </div>
        <div className="registration-filters">
          {["PENDING", "APPROVED", "ALL"].map((value) => (
            <button
              className={filter === value ? "active" : ""}
              onClick={() => setFilter(value)}
              key={value}
            >
              {value}
            </button>
          ))}
        </div>
      </div>

      <div className="registration-summary">
        <div>
          <span>Total Registrations</span>
          <b>{records.length}</b>
        </div>
        <div>
          <span>Awaiting Verification</span>
          <b>
            {
              records.filter(
                (item) =>
                  !(item.status === "APPROVED" && item.workStartApproved),
              ).length
            }
          </b>
        </div>
        <div>
          <span>Work Start Approved</span>
          <b>
            {
              records.filter(
                (item) => item.status === "APPROVED" && item.workStartApproved,
              ).length
            }
          </b>
        </div>
      </div>

      <div className="registration-table-wrap">
        <table className="registration-table">
          <thead>
            <tr>
              <th>PACS / Registration</th>
              <th>Location</th>
              <th>Contact Details</th>
              <th>Registered On</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {!visible.length && (
              <tr>
                <td colSpan="6">
                  <div className="empty-state">
                    <h2>No PACS registrations found</h2>
                    <p>
                      No record is available for the selected status and
                      allocated districts.
                    </p>
                  </div>
                </td>
              </tr>
            )}
            {visible.map((item) => {
              const approved =
                item.status === "APPROVED" &&
                item.isActive === true &&
                item.workStartApproved === true;
              return (
                <tr key={item.id}>
                  <td>
                    <div className="pacs-cell">
                      <div className="pacs-mark">
                        {String(getPacsName(item)).slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <b>{getPacsName(item)}</b>
                        <span>
                          {item.registrationNumber ||
                            item.pacsRegistrationNo ||
                            "Registration number pending"}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <b>
                      {item.district || "-"}, {item.state || "-"}
                    </b>
                    <span>
                      {[item.village, item.taluka || item.block]
                        .filter(Boolean)
                        .join(", ") || "Location not provided"}
                    </span>
                  </td>
                  <td>
                    <b>{item.name || item.contactPerson || "-"}</b>
                    <span>{item.mobile || item.phone || "-"}</span>
                    <span>{item.email || "-"}</span>
                  </td>
                  <td>
                    <b>{formatRegistrationDate(item.createdAt)}</b>
                    <span>Portal Registration</span>
                  </td>
                  <td>
                    <span
                      className={
                        approved
                          ? "table-status approved"
                          : "table-status pending"
                      }
                    >
                      {approved
                        ? "WORK START APPROVED"
                        : "VERIFICATION PENDING"}
                    </span>
                  </td>
                  <td>
                    {approved ? (
                      <div className="access-active">
                        <b>Access Active</b>
                        <span>Email queued</span>
                      </div>
                    ) : (
                      <button
                        className="approve-work"
                        disabled={approvingPacsId === item.id}
                        onClick={() => onApprove(item)}
                      >
                        {approvingPacsId === item.id
                          ? "Approving..."
                          : "Approve & Start Work"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function formatRegistrationDate(value) {
  if (!value) return "Not available";
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime())
    ? "Not available"
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
}

function NafedTenderPublisherPane({
  projects = [],
  profile = {},
  onPublished,
}) {
  const [selectedId, setSelectedId] = useState("");
  const [publishedTenders, setPublishedTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState({
    title: "",
    baseAmount: "",
    tenderFee: "2500",
    emdAmount: "",
    workDurationDays: "180",
    eligibility:
      "Eligible registered contractors with relevant warehouse/godown, civil or PEB construction experience and valid statutory registrations.",
  });

  const selected = projects.find((item) => item.id === selectedId) || null;

  const mouComplete = (project = {}) => Boolean(getCurrentFinalMouUrl(project));

  const paymentComplete = (project = {}) => {
    const values = [
      project.tenderPaymentStatus,
      project.tenderGenerationPaymentStatus,
      project.pacsTenderPaymentStatus,
      project.paymentStatus,
    ].map((value) => String(value || "").toUpperCase());
    return values.some((value) =>
      ["PAID", "VERIFIED", "RECEIVED", "COMPLETED", "SUCCESS"].includes(value),
    );
  };

  const technicalReady = (project = {}) =>
    project.technicalStage === "READY_FOR_TENDER" &&
    project.technicalWorkflow?.DPR?.status === "APPROVED" &&
    Number(
      project.approvedDprAmount ||
        project.finalDprAmount ||
        project.tenderEstimatedValue,
    ) > 0;

  const eligibility = (project = {}) => {
    const reasons = [];
    if (!mouComplete(project)) reasons.push("Final PMC-PACS two-party MOU is pending");
    if (!paymentComplete(project))
      reasons.push("PACS tender payment is not verified");
    if (!technicalReady(project))
      reasons.push("Approved estimate/BOQ is not available");
    if (project.tenderId || project.generatedTenderId)
      reasons.push("Tender already generated");
    return reasons;
  };

  const loadPublished = async () => {
    try {
      setLoading(true);
      const snap = await getDocs(collection(db, NAFED_COLLECTIONS.tenders));
      const allowed = new Set(projects.map((item) => item.id));
      const rows = snap.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .filter(
          (item) =>
            item.nafedProject === true && allowed.has(item.sourceProjectId),
        )
        .sort((a, b) =>
          String(b.publishedAtIso || "").localeCompare(
            String(a.publishedAtIso || ""),
          ),
        );
      setPublishedTenders(rows);
    } catch (error) {
      console.error(error);
      setNotice(error.message || "Published tenders could not be loaded.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPublished();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects.map((item) => item.id).join("|")]);

  useEffect(() => {
    if (!selected) return;
    const pacs = getPacsName(selected);
    const capacity = getCapacity(selected).toLocaleString("en-IN");
    setForm((current) => ({
      ...current,
      title:
        current.title ||
        `Construction of ${capacity} MT Grain Storage Godown for ${pacs}`,
      baseAmount: String(
        selected.approvedDprAmount ||
          selected.finalDprAmount ||
          selected.tenderEstimatedValue ||
          "",
      ),
      emdAmount: current.emdAmount || String(selected.emdAmount || ""),
      workDurationDays:
        current.workDurationDays || String(selected.workDurationDays || 180),
    }));
  }, [selected]);

  const generateTender = async () => {
    if (!selected || !auth.currentUser)
      return setNotice("Select an eligible NAFED project.");
    const blockers = eligibility(selected);
    if (blockers.length) return setNotice(blockers.join("; "));
    if (!form.title.trim())
      return setNotice("Enter tender title/name of work.");
    if (!(Number(form.baseAmount) > 0))
      return setNotice("Enter valid tender base amount excluding GST.");
    const lockedDprAmount = Number(
      selected.approvedDprAmount ||
        selected.finalDprAmount ||
        selected.tenderEstimatedValue ||
        0,
    );
    if (Number(form.baseAmount) !== lockedDprAmount)
      return setNotice(
        "Tender value must exactly match the PMC-approved final DPR amount.",
      );
    if (!(Number(form.emdAmount) > 0))
      return setNotice("Enter valid EMD amount.");
    if (
      !window.confirm(
        "Publish this tender to the existing All India Tender Portal? Bidding will close automatically after 7 complete days and sealed bids will be scheduled to open 24 hours later.",
      )
    )
      return;

    try {
      setBusy(true);
      setNotice("");
      const now = new Date();
      const closeAt = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const bidOpeningAt = new Date(closeAt.getTime() + 24 * 60 * 60 * 1000);
      const resultScheduledAt = new Date(
        bidOpeningAt.getTime() + 5 * 60 * 1000,
      );
      const pacsName = getPacsName(selected);
      const applicationId =
        selected.applicationId || selected.projectId || selected.id;
      const unit = {
        projectId: selected.id,
        applicationId,
        pacsName,
        capacityMt: getCapacity(selected),
        state: getState(selected),
        district: getDistrict(selected),
        taluka: selected.taluka || selected.block || "",
        village: selected.village || "",
        boqUrl: selected.boqUrl || selected.boqPdfUrl || "",
        estimateUrl: selected.estimateUrl || selected.estimatePdfUrl || "",
      };

      const tenderRef = await addDoc(
        collection(db, NAFED_COLLECTIONS.tenders),
        {
        title: form.title.trim(),
        ownerCompany: pacsName,
        organizationName: pacsName,
        city: getDistrict(selected),
        state: getState(selected),
        district: getDistrict(selected),
        baseAmount: Number(form.baseAmount),
        amount: Number(form.baseAmount),
        estimatedValueSource: "APPROVED_DPR",
        sourceDprAmount: lockedDprAmount,
        amountLockedFromDpr: true,
        amountExclusiveOfGst: true,
        gstExtraAsApplicable: true,
        tenderFee: Number(form.tenderFee || 2500),
        tenderFeeGstRate: 18,
        emdAmount: Number(form.emdAmount),
        workDurationDays: Number(form.workDurationDays || 180),
        eligibilityCriteria: form.eligibility.trim(),
        tenderMode: "single",
        isMultiUnit: false,
        tenderUnits: [unit],
        totalUnitCount: 1,
        linkedWorkflowProjectIds: [selected.id],
        sourceProjectId: selected.id,
        sourceApplicationId: applicationId,
        nafedProject: true,
        projectSource: "NAFED",
        applicationPortal: "NAFED_PACS_APPLY",
        tenderPortal: "EXISTING_ALL_INDIA_TENDER_PORTAL",
        allIndiaEligible: true,
        startAt: now.toISOString(),
        openDate: now.toISOString(),
        endAt: closeAt.toISOString(),
        closeDate: closeAt.toISOString(),
        bidOpeningAt: bidOpeningAt.toISOString(),
        resultScheduledAt: resultScheduledAt.toISOString(),
        biddingPeriodDays: 7,
        sealedPeriodHours: 24,
        isPublished: true,
        published: true,
        publicVisible: true,
        visibility: "public",
        statusFlag: "published",
        publishStatus: "published",
        finalStatus: "published",
        tenderStatus: "LIVE",
        bidSubmissionStatus: "OPEN",
        bidVisibility: "SEALED_UNTIL_OPENING",
        bidderIdentityVisibility: "HIDDEN_UNTIL_OPENING",
        comparativeStatementVisibility: "HIDDEN_UNTIL_RESULT",
        resultStatus: "SCHEDULED",
        resultVisibility: "HIDDEN",
        autoCloseEnabled: true,
        autoOpeningEnabled: true,
        autoResultEnabled: true,
        technicalQualificationRequired: true,
        tieHandling: "MANUAL_REVIEW_REQUIRED",
        lowestEligibleBidRule: "L1_AFTER_TECHNICAL_QUALIFICATION",
        generatedByPmcUid: auth.currentUser.uid,
        generatedByPmcEmail: auth.currentUser.email || "",
        generatedByPmcName:
          profile.organizationName || profile.name || "PMC Agency",
        publishedAt: serverTimestamp(),
        publishedAtIso: now.toISOString(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        },
      );

      await updateDoc(doc(db, NAFED_COLLECTIONS.godownProjects, selected.id), {
        tenderId: tenderRef.id,
        generatedTenderId: tenderRef.id,
        tenderNumber: `NAFED/${String(getState(selected)).slice(0, 2).toUpperCase()}/${now.getFullYear()}/${tenderRef.id.slice(0, 8).toUpperCase()}`,
        tenderStatus: "LIVE",
        tenderPublishedAt: serverTimestamp(),
        tenderPublishedAtIso: now.toISOString(),
        tenderCloseAt: closeAt.toISOString(),
        bidOpeningAt: bidOpeningAt.toISOString(),
        currentStage: "Tender Live - All India Portal",
        completedStep: Math.max(Number(selected.completedStep || 0), 4),
        progress: Math.max(Number(selected.progress || 0), 50),
        updatedAt: serverTimestamp(),
      });

      await setDoc(
        doc(
          db,
          NAFED_COLLECTIONS.activity,
          `${selected.id}_TENDER_PUBLISHED_${Date.now()}`,
        ),
        {
          action: "NAFED_TENDER_PUBLISHED_TO_ALL_INDIA_PORTAL",
          entityType: "TENDER",
          entityId: tenderRef.id,
          projectId: selected.id,
          applicationId,
          startAt: now.toISOString(),
          closeAt: closeAt.toISOString(),
          bidOpeningAt: bidOpeningAt.toISOString(),
          performedByUid: auth.currentUser.uid,
          performedByEmail: auth.currentUser.email || "",
          performedByRole: "PMC",
          createdAt: serverTimestamp(),
        },
      );

      setNotice(
        `Tender published successfully. It will close on ${closeAt.toLocaleString("en-IN")} and bids are scheduled to open on ${bidOpeningAt.toLocaleString("en-IN")}.`,
      );
      onPublished?.(selected.id, tenderRef.id);
      await loadPublished();
    } catch (error) {
      console.error(error);
      setNotice(error.message || "Tender generation failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ntp">
      <style>{`
    .ntp{font-family:Inter,Segoe UI,Arial;color:#102744}.ntp-head{display:flex;justify-content:space-between;gap:20px;padding:20px;border-radius:17px;color:#fff;background:linear-gradient(125deg,#062e64,#0759aa 55%,#087746)}.ntp-head small{color:#a9efc4;font-weight:900}.ntp-head h2{margin:5px 0}.ntp-head p{margin:0;color:#d8e8f8}.ntp-link{align-self:center;border:1px solid #7fd2a2;border-radius:9px;padding:11px 14px;background:#12a052;color:#fff;text-decoration:none;font-weight:900}.ntp-msg{margin:13px 0;padding:12px;border:1px solid #b9d8f2;border-radius:9px;background:#edf7ff;color:#14538d;font-weight:700}.ntp-layout{display:grid;grid-template-columns:330px 1fr;gap:13px;margin-top:13px}.ntp-card{border:1px solid #d5e0eb;border-radius:13px;background:#fff;padding:15px}.ntp-card h3{margin:0 0 12px}.ntp-project{display:block;width:100%;margin-bottom:8px;padding:11px;border:1px solid #d8e2ec;border-radius:9px;background:#fff;text-align:left;color:#193b5c}.ntp-project.active{border-color:#087746;background:#edf8f1}.ntp-project b,.ntp-project span,.ntp-project small{display:block}.ntp-project span{margin-top:4px;color:#65758a}.ntp-project small{margin-top:5px;color:#9a5d00}.ntp-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:10px}.ntp-field span{display:block;margin-bottom:5px;color:#5c6d82;font-size:9px;font-weight:900;text-transform:uppercase}.ntp-field input,.ntp-field textarea{width:100%;border:1px solid #cad7e5;border-radius:8px;padding:10px}.ntp-field.wide{grid-column:1/-1}.ntp-checks{display:flex;flex-wrap:wrap;gap:7px;margin:12px 0}.ntp-checks span{padding:7px 9px;border-radius:20px;background:#fff0dc;color:#9a5d00;font-size:9px}.ntp-checks span.ok{background:#e7f7ed;color:#08743a}.ntp-generate{border:0;border-radius:9px;padding:12px 16px;background:#087746;color:#fff;font-weight:900}.ntp-generate:disabled{opacity:.5}.ntp-table{width:100%;border-collapse:collapse}.ntp-table th,.ntp-table td{padding:10px;border-bottom:1px solid #e1e8ef;text-align:left;font-size:10px}.ntp-table th{background:#f1f6fa;color:#5e7085}.ntp-status{padding:6px 8px;border-radius:20px;background:#e7f7ed;color:#08743a;font-weight:900}.ntp-empty{padding:25px;text-align:center;color:#718095}@media(max-width:850px){.ntp-layout{grid-template-columns:1fr}.ntp-grid{grid-template-columns:1fr}.ntp-field.wide{grid-column:auto}.ntp-head{display:block}.ntp-link{display:inline-block;margin-top:12px}}
  `}</style>
      <div className="ntp-head">
        <div>
          <small>EXISTING TENDER PORTAL CONNECTION</small>
          <h2>NAFED All India Tender Publisher</h2>
          <p>
            Eligible NAFED projects are published directly to the existing
            vendor tender portal.
          </p>
        </div>
        <a
          className="ntp-link"
          href="/tender/list"
          target="_blank"
          rel="noreferrer"
        >
          Open Public Tender List
        </a>
      </div>
      {notice && <div className="ntp-msg">{notice}</div>}
      <div className="ntp-layout">
        <div className="ntp-card">
          <h3>NAFED Projects</h3>
          {projects.map((project) => {
            const blockers = eligibility(project);
            return (
              <button
                className={
                  selectedId === project.id
                    ? "ntp-project active"
                    : "ntp-project"
                }
                onClick={() => {
                  setSelectedId(project.id);
                  setForm((old) => ({
                    ...old,
                    title: "",
                    baseAmount: "",
                    emdAmount: "",
                  }));
                }}
                key={project.id}
              >
                <b>{getPacsName(project)}</b>
                <span>
                  {project.applicationId || project.id} ·{" "}
                  {formatCapacity(getCapacity(project))}
                </span>
                <small>
                  {blockers.length ? blockers.join(" · ") : "Ready to publish"}
                </small>
              </button>
            );
          })}
          {!projects.length && (
            <div className="ntp-empty">
              No allocated NAFED project available.
            </div>
          )}
        </div>
        <div className="ntp-card">
          <h3>Tender Generation</h3>
          {selected ? (
            <>
              <div className="ntp-checks">
                <span className={mouComplete(selected) ? "ok" : ""}>
                  {mouComplete(selected) ? "✓" : "○"} Final MOU
                </span>
                <span className={paymentComplete(selected) ? "ok" : ""}>
                  {paymentComplete(selected) ? "✓" : "○"} Payment Verified
                </span>
                <span className={technicalReady(selected) ? "ok" : ""}>
                  {technicalReady(selected) ? "✓" : "○"} Estimate & BOQ
                </span>
              </div>
              <div className="ntp-grid">
                <label className="ntp-field wide">
                  <span>Name of Work</span>
                  <input
                    value={form.title}
                    onChange={(e) =>
                      setForm({ ...form, title: e.target.value })
                    }
                  />
                </label>
                <label className="ntp-field">
                  <span>Base Amount (Excluding GST)</span>
                  <input
                    type="number"
                    value={form.baseAmount}
                    readOnly
                    title="Automatically locked from PMC-approved final DPR amount"
                  />
                </label>
                <label className="ntp-field">
                  <span>EMD Amount</span>
                  <input
                    type="number"
                    value={form.emdAmount}
                    onChange={(e) =>
                      setForm({ ...form, emdAmount: e.target.value })
                    }
                  />
                </label>
                <label className="ntp-field">
                  <span>Tender Fee (Before GST)</span>
                  <input
                    type="number"
                    value={form.tenderFee}
                    onChange={(e) =>
                      setForm({ ...form, tenderFee: e.target.value })
                    }
                  />
                </label>
                <label className="ntp-field">
                  <span>Work Duration (Days)</span>
                  <input
                    type="number"
                    value={form.workDurationDays}
                    onChange={(e) =>
                      setForm({ ...form, workDurationDays: e.target.value })
                    }
                  />
                </label>
                <label className="ntp-field wide">
                  <span>Eligibility Criteria</span>
                  <textarea
                    rows="4"
                    value={form.eligibility}
                    onChange={(e) =>
                      setForm({ ...form, eligibility: e.target.value })
                    }
                  />
                </label>
              </div>
              <button
                className="ntp-generate"
                disabled={busy || eligibility(selected).length > 0}
                onClick={generateTender}
              >
                {busy
                  ? "Publishing Tender..."
                  : "Generate & Publish All India Tender"}
              </button>
            </>
          ) : (
            <div className="ntp-empty">Select a NAFED project.</div>
          )}
        </div>
      </div>
      <div className="ntp-card" style={{ marginTop: 13 }}>
        <h3>Generated NAFED Tenders</h3>
        {loading ? (
          <div className="ntp-empty">Loading tenders...</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="ntp-table">
              <thead>
                <tr>
                  <th>Tender / PACS</th>
                  <th>Amount</th>
                  <th>Published</th>
                  <th>Closing</th>
                  <th>Bid Opening</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {publishedTenders.map((tender) => (
                  <tr key={tender.id}>
                    <td>
                      <b>{tender.title}</b>
                      <br />
                      {tender.ownerCompany}
                    </td>
                    <td>
                      ₹ {Number(tender.baseAmount || 0).toLocaleString("en-IN")}
                    </td>
                    <td>{formatRegistrationDate(tender.startAt)}</td>
                    <td>{formatRegistrationDate(tender.endAt)}</td>
                    <td>{formatRegistrationDate(tender.bidOpeningAt)}</td>
                    <td>
                      <span className="ntp-status">
                        {tender.tenderStatus || "LIVE"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!publishedTenders.length && (
              <div className="ntp-empty">
                No NAFED tender has been generated yet.
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

const upper = (value = "") => String(value || "").trim().toUpperCase();

const assuranceText = (value = "") => String(value ?? "").trim();

const safeAssuranceFileName = (value, fallback = "NAFED_Assurance_Letter.pdf") => {
  const name = assuranceText(value || fallback)
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\s+/g, "_")
    .slice(0, 150);
  return /\.pdf$/i.test(name) ? name : `${name}.pdf`;
};

const assurancePacsUid = (project = {}) =>
  assuranceText(
    project.pacsUid ||
      project.pacsUserUid ||
      project.pacsUserId ||
      project.pacsId ||
      project.applicantUid ||
      project.submittedByUid ||
      project.createdByUid ||
      project.profileId,
  );

const assurancePacsEmail = (project = {}) =>
  assuranceText(
    project.pacsEmail ||
      project.applicantEmail ||
      project.contactEmail ||
      project.email,
  );

const inputDate = (value) => {
  if (!value) return "";
  const date = value?.toDate instanceof Function ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

const assuranceSha256 = async (file) => {
  if (!globalThis.crypto?.subtle) return "";
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    await file.arrayBuffer(),
  );
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

function PmcAssuranceLetterDesk({
  projects = [],
  profile = {},
  onProjectUpdated,
}) {
  const [selectedId, setSelectedId] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [file, setFile] = useState(null);
  const [letterNumber, setLetterNumber] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [branchName, setBranchName] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);

  const eligibleProjects = useMemo(
    () =>
      projects
        .filter((project) => isStrictNafedApplyProject(project))
        .filter((project) => !isDeletedRecord(project))
        .sort((a, b) => getPacsName(a).localeCompare(getPacsName(b))),
    [projects],
  );

  const statusOf = (project) =>
    getNafedAssuranceLetter(project) ? "PUBLISHED" : "AWAITING_UPLOAD";

  const visibleProjects = useMemo(
    () =>
      eligibleProjects.filter(
        (project) => filter === "ALL" || statusOf(project) === filter,
      ),
    [eligibleProjects, filter],
  );

  useEffect(() => {
    if (
      selectedId &&
      eligibleProjects.some((project) => project.id === selectedId)
    ) {
      return;
    }
    const next =
      eligibleProjects.find((project) => !getNafedAssuranceLetter(project)) ||
      eligibleProjects[0];
    setSelectedId(next?.id || "");
  }, [eligibleProjects, selectedId]);

  const selected =
    eligibleProjects.find((project) => project.id === selectedId) || null;
  const currentLetter = selected ? getNafedAssuranceLetter(selected) : null;

  useEffect(() => {
    setFile(null);
    setFileInputKey((value) => value + 1);
    setConfirmed(false);
    setLetterNumber(
      assuranceText(
        currentLetter?.letterNumber ||
          currentLetter?.assuranceNo ||
          selected?.nafedAssuranceNo,
      ),
    );
    setIssueDate(
      inputDate(
        currentLetter?.issueDate ||
          currentLetter?.assuranceDate ||
          selected?.nafedAssuranceDate,
      ),
    );
    setBranchName(
      assuranceText(
        currentLetter?.branchName ||
          currentLetter?.nafedBranchName ||
          selected?.nafedBranchName ||
          `${selected?.state || profile.state || ""} State Branch`,
      ),
    );
  }, [selectedId, currentLetter?.url]);

  const counts = {
    total: eligibleProjects.length,
    awaiting: eligibleProjects.filter(
      (project) => statusOf(project) === "AWAITING_UPLOAD",
    ).length,
    published: eligibleProjects.filter(
      (project) => statusOf(project) === "PUBLISHED",
    ).length,
    mouComplete: eligibleProjects.filter(isOfficialMouExecuted).length,
  };

  const downloadLetter = async () => {
    if (!currentLetter?.url) return;
    try {
      const response = await fetch(currentLetter.url);
      if (!response.ok) throw new Error("Direct download failed.");
      const blobUrl = URL.createObjectURL(await response.blob());
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = safeAssuranceFileName(
        currentLetter.fileName,
        `NAFED_Assurance_Letter_${selected?.applicationId || selected?.id}.pdf`,
      );
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
    } catch (error) {
      console.error("Assurance Letter direct download failed", error);
      window.open(currentLetter.url, "_blank", "noopener,noreferrer");
      toast.error("Direct download was blocked; the letter was opened instead.");
    }
  };

  const publishLetter = async () => {
    const user = auth.currentUser;
    if (!selected || !user) {
      return toast.error("PMC login session or PACS project is unavailable.");
    }
    if (getAllocatedPmcUid(selected) !== user.uid) {
      return toast.error("This PACS project is not allocated to your PMC.");
    }
    if (!file) return toast.error("Select the official NAFED Assurance Letter PDF.");
    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) {
      return toast.error("Only a PDF Assurance Letter is allowed.");
    }
    if (file.size > 25 * 1024 * 1024) {
      return toast.error("Assurance Letter PDF must be 25 MB or less.");
    }
    if (!assuranceText(letterNumber)) {
      return toast.error("Enter the NAFED letter/reference number.");
    }
    if (!issueDate) return toast.error("Select the NAFED issue date.");
    if (!assuranceText(branchName)) {
      return toast.error("Enter the issuing NAFED State Branch.");
    }
    if (!confirmed) {
      return toast.error("Confirm that this is the official NAFED-issued PDF.");
    }

    const replacing = Boolean(currentLetter?.url);
    const approved = window.confirm(
      replacing
        ? "Publish this as the next Assurance Letter version? PACS will receive the new active version immediately."
        : "Publish this official NAFED Assurance Letter to the selected PACS?",
    );
    if (!approved) return;

    setBusy(true);
    try {
      const version = Number(
        currentLetter?.version || currentLetter?.documentVersion || 0,
      ) + 1;
      const fileName = safeAssuranceFileName(
        file.name,
        `NAFED_Assurance_Letter_${selected.applicationId || selected.id}.pdf`,
      );
      const uploadedAtIso = new Date().toISOString();
      const storagePath = [
        "nafed_pmc",
        user.uid,
        selected.id,
        "nafed_assurance_letters",
        `v${version}_${Date.now()}_${fileName}`,
      ].join("/");
      const sha256 = await assuranceSha256(file);
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, {
        contentType: "application/pdf",
        customMetadata: {
          projectId: selected.id,
          applicationId: assuranceText(selected.applicationId || selected.id),
          pacsUid: assurancePacsUid(selected),
          documentType: "NAFED_ASSURANCE_LETTER",
          uploadedByUid: user.uid,
          uploadedByRole: "PMC",
          version: String(version),
          sha256: sha256 || "NOT_AVAILABLE",
        },
      });
      const url = await getDownloadURL(storageRef);
      const record = {
        type: "NAFED_ASSURANCE_LETTER",
        code: "NAL",
        title: "NAFED Assurance Letter",
        letterNumber: assuranceText(letterNumber),
        assuranceNo: assuranceText(letterNumber),
        issueDate,
        assuranceDate: issueDate,
        branchName: assuranceText(branchName),
        nafedBranchName: assuranceText(branchName),
        url,
        downloadURL: url,
        fileName,
        storagePath,
        contentType: "application/pdf",
        size: file.size,
        sha256,
        version,
        documentVersion: version,
        status: "PUBLISHED_TO_PACS",
        source: "NAFED_LETTER_UPLOADED_BY_PMC",
        officialAuthority: "NAFED",
        publishedToPacs: true,
        pacsUid: assurancePacsUid(selected),
        pacsName: getPacsName(selected),
        applicationId: selected.applicationId || selected.id,
        uploadedByUid: user.uid,
        uploadedByEmail: user.email || "",
        uploadedByName:
          profile.organizationName ||
          profile.agencyName ||
          profile.name ||
          user.displayName ||
          "PMC Authorised Officer",
        uploadedByRole: "PMC",
        uploadedAtIso,
      };

      await updateDoc(doc(db, NAFED_COLLECTIONS.godownProjects, selected.id), {
        "pmcVerificationDocuments.NAFED_ASSURANCE_LETTER": record,
        "pmcTechnicalDocuments.NAFED_ASSURANCE_LETTER": record,
        nafedAssuranceLetter: record,
        nafedAssuranceLetterUrl: url,
        hiringAssuranceLetterUrl: url,
        assuranceLetterUrl: url,
        nafedAssuranceStatus: "RECEIVED",
        assurancePublicationStatus: "PUBLISHED_TO_PACS",
        nafedAssuranceNo: assuranceText(letterNumber),
        nafedAssuranceDate: issueDate,
        nafedBranchName: assuranceText(branchName),
        assuranceLetterPublishedByPmcUid: user.uid,
        assuranceLetterPublishedByPmcEmail: user.email || "",
        assuranceLetterPublishedAt: serverTimestamp(),
        assuranceLetterHistory: arrayUnion({
          action: replacing ? "VERSION_REPLACED" : "PUBLISHED_TO_PACS",
          version,
          url,
          storagePath,
          fileName,
          sha256,
          letterNumber: assuranceText(letterNumber),
          issueDate,
          performedByUid: user.uid,
          performedByEmail: user.email || "",
          performedAtIso: uploadedAtIso,
        }),
        updatedAt: serverTimestamp(),
      });

      const localChanges = {
        pmcVerificationDocuments: {
          ...(selected.pmcVerificationDocuments || {}),
          NAFED_ASSURANCE_LETTER: record,
        },
        pmcTechnicalDocuments: {
          ...(selected.pmcTechnicalDocuments || {}),
          NAFED_ASSURANCE_LETTER: record,
        },
        nafedAssuranceLetter: record,
        nafedAssuranceLetterUrl: url,
        hiringAssuranceLetterUrl: url,
        assuranceLetterUrl: url,
        nafedAssuranceStatus: "RECEIVED",
        assurancePublicationStatus: "PUBLISHED_TO_PACS",
        nafedAssuranceNo: assuranceText(letterNumber),
        nafedAssuranceDate: issueDate,
        nafedBranchName: assuranceText(branchName),
      };
      onProjectUpdated?.(selected.id, localChanges);

      try {
        await addDoc(collection(db, NAFED_COLLECTIONS.notifications), {
          type: "NAFED_ASSURANCE_LETTER_PUBLISHED",
          title: "NAFED Assurance Letter Available",
          message: `${getPacsName(selected)} can now view and download the official NAFED Assurance Letter.`,
          projectId: selected.id,
          applicationId: selected.applicationId || selected.id,
          pacsUid: assurancePacsUid(selected),
          pacsEmail: assurancePacsEmail(selected),
          pmcUid: user.uid,
          visibleTo: ["PACS", "PMC", "STATE_BRANCH", "HEAD_OFFICE"],
          readBy: [],
          status: "ACTIVE",
          createdAt: serverTimestamp(),
        });
      } catch (notificationError) {
        console.error("Assurance Letter notification queue failed", notificationError);
      }

      try {
        await addDoc(collection(db, NAFED_COLLECTIONS.activity), {
          action: "NAFED_ASSURANCE_LETTER_PUBLISHED",
          entityType: "GODOWN_PROJECT",
          entityId: selected.id,
          entityName: getPacsName(selected),
          documentVersion: version,
          performedByUid: user.uid,
          performedByEmail: user.email || "",
          performedByRole: "PMC",
          headOfficeVisible: true,
          stateBranchVisible: true,
          createdAt: serverTimestamp(),
        });
      } catch (activityError) {
        console.error("Assurance Letter activity log failed", activityError);
      }

      setFile(null);
      setConfirmed(false);
      setFileInputKey((value) => value + 1);
      toast.success(
        replacing
          ? `Assurance Letter Version ${version} published to PACS.`
          : "NAFED Assurance Letter published to PACS.",
      );
    } catch (error) {
      console.error("NAFED Assurance Letter publication failed", error);
      const message =
        error?.code === "storage/unauthorized" ||
        error?.code === "permission-denied" ||
        error?.code === "firestore/permission-denied"
          ? "Upload permission was denied for this allocated PMC project."
          : error?.message || "Assurance Letter publication failed.";
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="assurance-desk">
      <style>{ASSURANCE_DESK_STYLE}</style>
      <header className="assurance-hero">
        <div>
          <small>OFFICIAL NAFED DOCUMENT CONTROL</small>
          <h2>NAFED Assurance Letter Publication Desk</h2>
          <p>
            Upload the letter issued by NAFED, verify its official particulars
            and publish the same PDF directly to the allocated PACS portal.
          </p>
        </div>
        <span className="assurance-control">PMC CONTROLLED</span>
      </header>

      <div className="assurance-kpis">
        <article><span>Allocated Projects</span><b>{counts.total}</b></article>
        <article className="pending"><span>Awaiting Letter</span><b>{counts.awaiting}</b></article>
        <article className="complete"><span>Published to PACS</span><b>{counts.published}</b></article>
        <article><span>MOU Completed</span><b>{counts.mouComplete}</b></article>
      </div>

      <div className="assurance-filters">
        {[
          ["ALL", "All Projects"],
          ["AWAITING_UPLOAD", "Awaiting Upload"],
          ["PUBLISHED", "Published"],
        ].map(([value, label]) => (
          <button
            type="button"
            key={value}
            className={filter === value ? "active" : ""}
            onClick={() => setFilter(value)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="assurance-layout">
        <aside className="assurance-projects">
          {!visibleProjects.length && (
            <div className="assurance-empty">No PACS project matches this filter.</div>
          )}
          {visibleProjects.map((project) => {
            const published = Boolean(getNafedAssuranceLetter(project));
            return (
              <button
                type="button"
                key={project.id}
                className={selectedId === project.id ? "selected" : ""}
                onClick={() => setSelectedId(project.id)}
              >
                <b>{getPacsName(project)}</b>
                <span>{project.applicationId || project.id}</span>
                <small>
                  {formatCapacity(getCapacity(project))} · {getDistrict(project) || "District pending"}
                </small>
                <em className={published ? "published" : "waiting"}>
                  {published ? "PUBLISHED TO PACS" : "AWAITING NAFED LETTER"}
                </em>
              </button>
            );
          })}
        </aside>

        <main className="assurance-workspace">
          {!selected ? (
            <div className="assurance-empty">Select an allocated PACS project.</div>
          ) : (
            <>
              <div className="assurance-case-head">
                <div>
                  <small>SELECTED PACS / PROJECT OWNER</small>
                  <h3>{getPacsName(selected)}</h3>
                  <p>
                    {selected.applicationId || selected.id} · {getDistrict(selected) || "District pending"}, {getState(selected) || "State pending"}
                  </p>
                </div>
                <span className={currentLetter ? "ready" : "waiting"}>
                  {currentLetter ? "LETTER AVAILABLE" : "UPLOAD REQUIRED"}
                </span>
              </div>

              {currentLetter && (
                <section className="current-assurance">
                  <div>
                    <small>CURRENT ACTIVE VERSION</small>
                    <h4>{currentLetter.letterNumber || currentLetter.assuranceNo || "NAFED Assurance Letter"}</h4>
                    <p>
                      Version {currentLetter.version || currentLetter.documentVersion || 1} · {currentLetter.branchName || currentLetter.nafedBranchName || selected.nafedBranchName || "NAFED State Branch"}
                    </p>
                  </div>
                  <div className="current-actions">
                    <button type="button" onClick={() => window.open(currentLetter.url, "_blank", "noopener,noreferrer")}>View Letter</button>
                    <button type="button" onClick={downloadLetter}>Download PDF</button>
                  </div>
                </section>
              )}

              <section className="assurance-form">
                <div className="assurance-form-title">
                  <span>01</span>
                  <div>
                    <small>{currentLetter ? "VERSION CONTROL" : "NEW PUBLICATION"}</small>
                    <h3>{currentLetter ? "Replace with New Official Version" : "Publish Official Assurance Letter"}</h3>
                  </div>
                </div>

                <div className="assurance-fields">
                  <label>
                    <span>NAFED Letter / Reference Number *</span>
                    <input value={letterNumber} onChange={(event) => setLetterNumber(event.target.value)} placeholder="Example: NAFED/GUJ/2026/001" />
                  </label>
                  <label>
                    <span>Issue Date *</span>
                    <input type="date" value={issueDate} onChange={(event) => setIssueDate(event.target.value)} />
                  </label>
                  <label>
                    <span>Issuing NAFED State Branch *</span>
                    <input value={branchName} onChange={(event) => setBranchName(event.target.value)} placeholder="Gujarat State Branch" />
                  </label>
                </div>

                <label className="assurance-file">
                  <span>Official NAFED Assurance Letter PDF * <small>Maximum 25 MB</small></span>
                  <input
                    key={fileInputKey}
                    type="file"
                    accept="application/pdf,.pdf"
                    disabled={busy}
                    onChange={(event) => setFile(event.target.files?.[0] || null)}
                  />
                  <em>{file ? `${file.name} · ${(file.size / 1024 / 1024).toFixed(2)} MB` : "No PDF selected"}</em>
                </label>

                <label className="assurance-confirm">
                  <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
                  <span>
                    I confirm that this is the complete official letter issued by
                    NAFED for the selected PACS and it may be published unchanged
                    to the PACS, State Branch and audit record.
                  </span>
                </label>

                <div className="assurance-publish-row">
                  <div>
                    <b>Direct PACS Publication</b>
                    <span>No PACS re-upload is required. Previous versions remain in audit history.</span>
                  </div>
                  <button type="button" disabled={busy || !file || !confirmed} onClick={publishLetter}>
                    {busy ? "Uploading & Publishing..." : currentLetter ? "Publish New Version to PACS" : "Publish Assurance Letter to PACS"}
                  </button>
                </div>
              </section>
            </>
          )}
        </main>
      </div>
    </section>
  );
}

const ASSURANCE_DESK_STYLE = `
.assurance-desk{display:grid;gap:14px;color:#132b47;font-family:Inter,"Segoe UI",Arial,sans-serif}.assurance-desk *{box-sizing:border-box}.assurance-hero{display:flex;justify-content:space-between;align-items:center;gap:20px;padding:24px 26px;border:1px solid #cbd9e7;border-top:5px solid #d69a2d;border-radius:14px;background:linear-gradient(120deg,#0b315d,#0a6044);color:#fff;box-shadow:0 16px 36px rgba(15,41,68,.14)}.assurance-hero small,.assurance-form-title small,.assurance-case-head small,.current-assurance small{font-size:9px;font-weight:950;letter-spacing:.1em}.assurance-hero small{color:#a7efc2}.assurance-hero h2{margin:6px 0;font-size:25px}.assurance-hero p{max-width:790px;margin:0;color:#e1f2ea;font-size:12px;line-height:1.55}.assurance-control{padding:9px 12px;border:1px solid rgba(255,255,255,.35);border-radius:999px;background:rgba(255,255,255,.12);font-size:9px;font-weight:950;white-space:nowrap}.assurance-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:11px}.assurance-kpis article{padding:16px 17px;border:1px solid #d5e0eb;border-radius:12px;background:#fff;box-shadow:0 8px 22px rgba(15,41,68,.05)}.assurance-kpis span,.assurance-kpis b{display:block}.assurance-kpis span{color:#65788d;font-size:9px;font-weight:900;text-transform:uppercase}.assurance-kpis b{margin-top:6px;color:#11345d;font-size:25px}.assurance-kpis .pending{border-top:3px solid #d49725}.assurance-kpis .complete{border-top:3px solid #1d7b4b}.assurance-filters{display:flex;gap:7px;padding:10px 12px;border:1px solid #d5e0eb;border-radius:10px;background:#fff}.assurance-filters button{padding:8px 11px;border:1px solid #bdcddd;border-radius:7px;background:#fff;color:#38526e;font-size:9px;font-weight:900;cursor:pointer}.assurance-filters button.active{border-color:#123b69;background:#123b69;color:#fff}.assurance-layout{display:grid;grid-template-columns:325px minmax(0,1fr);gap:13px}.assurance-projects,.assurance-workspace{border:1px solid #d2deea;border-radius:13px;background:#fff;box-shadow:0 10px 28px rgba(15,41,68,.06)}.assurance-projects{padding:11px;max-height:780px;overflow:auto}.assurance-projects>button{display:block;width:100%;margin-bottom:8px;padding:12px;border:1px solid #d8e2eb;border-left:4px solid transparent;border-radius:8px;background:#fff;color:#183451;text-align:left;cursor:pointer}.assurance-projects>button.selected{border-color:#4486c2;border-left-color:#176c46;background:#f1f7fc}.assurance-projects b,.assurance-projects span,.assurance-projects small,.assurance-projects em{display:block}.assurance-projects span{margin-top:4px;color:#536b83;font-size:9px}.assurance-projects small{margin-top:4px;color:#74859a;font-size:9px}.assurance-projects em{width:max-content;margin-top:7px;padding:5px 7px;border-radius:999px;font-size:8px;font-style:normal;font-weight:950}.assurance-projects em.published{background:#e5f6ea;color:#176d40}.assurance-projects em.waiting{background:#fff2da;color:#9a5d00}.assurance-workspace{padding:14px}.assurance-empty{padding:45px 18px;color:#718297;text-align:center}.assurance-case-head{display:flex;align-items:center;justify-content:space-between;gap:15px;padding:17px 18px;border-left:5px solid #1b7048;background:#f3f7fb}.assurance-case-head small{color:#1b7048}.assurance-case-head h3{margin:5px 0 3px;font-size:19px}.assurance-case-head p{margin:0;color:#63778d;font-size:10px}.assurance-case-head>span{padding:7px 9px;border-radius:999px;font-size:8px;font-weight:950}.assurance-case-head>span.ready{background:#e3f5e8;color:#176d40}.assurance-case-head>span.waiting{background:#fff0d5;color:#965a00}.current-assurance{display:flex;justify-content:space-between;align-items:center;gap:15px;margin-top:12px;padding:15px 17px;border:1px solid #9fd0ae;border-radius:10px;background:#f0faf3}.current-assurance small{color:#247149}.current-assurance h4{margin:4px 0;color:#153b5f;font-size:15px}.current-assurance p{margin:0;color:#607487;font-size:10px}.current-actions{display:flex;gap:7px}.current-actions button{padding:8px 10px;border:1px solid #9eb7ce;border-radius:7px;background:#fff;color:#173c68;font-size:9px;font-weight:900;cursor:pointer}.assurance-form{margin-top:12px;border:1px solid #ccd9e6;border-radius:11px;overflow:hidden}.assurance-form-title{display:flex;align-items:center;gap:11px;padding:14px 16px;border-bottom:1px solid #d6e1eb;background:#edf3f8}.assurance-form-title>span{display:grid;width:34px;height:34px;place-items:center;border-radius:50%;background:#103a69;color:#fff;font-size:10px;font-weight:950}.assurance-form-title small{color:#29714d}.assurance-form-title h3{margin:4px 0 0;font-size:16px}.assurance-fields{display:grid;grid-template-columns:1.2fr .7fr 1fr;gap:10px;padding:15px}.assurance-fields label>span,.assurance-file>span{display:block;margin-bottom:6px;color:#405a75;font-size:9px;font-weight:900;text-transform:uppercase}.assurance-fields input,.assurance-file input{width:100%;min-height:42px;padding:9px 10px;border:1px solid #adbdcc;border-radius:7px;background:#fff;color:#183451}.assurance-file{display:block;margin:0 15px 14px;padding:13px;border:1px dashed #9fb4c8;border-radius:8px;background:#f8fafc}.assurance-file>span small{float:right;color:#79889a}.assurance-file em{display:block;margin-top:7px;color:#667a8f;font-size:9px;font-style:normal}.assurance-confirm{display:flex;align-items:flex-start;gap:9px;margin:0 15px 14px;padding:12px;border:1px solid #cad7e3;background:#f4f7fa;color:#526980;font-size:10px;line-height:1.45}.assurance-confirm input{margin-top:2px}.assurance-publish-row{display:flex;justify-content:space-between;align-items:center;gap:15px;padding:14px 15px;border-top:1px solid #d2dde8;background:#eef7f1}.assurance-publish-row b,.assurance-publish-row span{display:block}.assurance-publish-row b{color:#176e43;font-size:11px}.assurance-publish-row span{margin-top:4px;color:#687b8e;font-size:9px}.assurance-publish-row button{padding:11px 14px;border:0;border-radius:8px;background:linear-gradient(135deg,#176e43,#154c7e);color:#fff;font-size:10px;font-weight:950;cursor:pointer}.assurance-publish-row button:disabled{cursor:not-allowed;opacity:.48}@media(max-width:1000px){.assurance-layout{grid-template-columns:1fr}.assurance-projects{max-height:280px}.assurance-fields{grid-template-columns:1fr}.assurance-kpis{grid-template-columns:repeat(2,1fr)}}@media(max-width:650px){.assurance-hero,.assurance-case-head,.current-assurance,.assurance-publish-row{align-items:flex-start;flex-direction:column}.assurance-kpis{grid-template-columns:1fr}.current-actions,.current-actions button,.assurance-publish-row button{width:100%}}
`;

function isPmcDocumentSetApproved(project = {}) {
  return (
    project.documentStage === "PMC_DOCUMENTS_APPROVED" ||
    ["APPROVED", "APPROVED_TO_WORKFLOW"].includes(
      upper(project.pmcDecision),
    ) ||
    ["APPROVED", "APPROVED_TO_WORKFLOW"].includes(
      upper(project.pmcVerificationStatus),
    ) ||
    upper(project.pmcCompleteDocumentSetStatus) === "APPROVED"
  );
}

/**
 * Reads old and new MOU records as one controlled workflow. Signature facts
 * take priority over a stale status string so an existing case is never lost.
 */
function getCanonicalMouStatus(project = {}) {
  const state = getMouSigningState(project);
  if (state.pmc) return state.status;
  if (isPmcDocumentSetApproved(project) || project.mouSignatures?.pmc?.status === "SIGNED" ||
      project.mouSignatures?.pacs?.status === "SIGNED" || project.mouDraftUrl) return "PENDING_PMC_SIGNATURE";
  return "DOCUMENT_VERIFICATION_PENDING";
}

function PmcMouExecutionDesk({
  projects = [],
  profile = {},
  onProjectUpdated,
}) {
  const mouProjects = useMemo(
    () =>
      projects
        .filter((project) => {
          return (
            getCanonicalMouStatus(project) !==
            "DOCUMENT_VERIFICATION_PENDING"
          );
        })
        .sort((a, b) => {
          const rank = (project) => {
            const status = getCanonicalMouStatus(project);
            return {
              PENDING_PMC_SIGNATURE: 0,
              PENDING_PACS_SIGNATURE: 1,
              FULLY_EXECUTED: 2,
            }[status] ?? 4;
          };
          return rank(a) - rank(b);
        }),
    [projects],
  );

  const [selectedId, setSelectedId] = useState("");
  const [filter, setFilter] = useState("ALL");

  useEffect(() => {
    if (selectedId && mouProjects.some((project) => project.id === selectedId))
      return;
    const priority =
      mouProjects.find(
        (project) =>
          getCanonicalMouStatus(project) === "PENDING_PMC_SIGNATURE",
      ) || mouProjects[0];
    setSelectedId(priority?.id || "");
  }, [mouProjects, selectedId]);

  const statusOf = getCanonicalMouStatus;

  const visible = mouProjects.filter((project) => {
    const status = statusOf(project);
    if (filter === "PMC_PENDING") return status === "PENDING_PMC_SIGNATURE";
    if (filter === "PACS") return status === "PENDING_PACS_SIGNATURE";
    if (filter === "EXECUTED") return status === "FULLY_EXECUTED";
    return true;
  });

  const selected =
    mouProjects.find((project) => project.id === selectedId) || null;
  const counts = {
    total: mouProjects.length,
    pmc: mouProjects.filter(
      (project) => statusOf(project) === "PENDING_PMC_SIGNATURE",
    ).length,
    pacs: mouProjects.filter(
      (project) => statusOf(project) === "PENDING_PACS_SIGNATURE",
    ).length,
    executed: mouProjects.filter(
      (project) => statusOf(project) === "FULLY_EXECUTED",
    ).length,
  };

  const applyUpdate = (changes) => {
    if (!selected) return;
    onProjectUpdated?.(selected.id, changes);
  };

  return (
    <section className="mou-desk">
      <style>{MOU_DESK_STYLE}</style>
      <div className="mou-desk-head">
        <div>
          <small>CONTROLLED TWO-PARTY EXECUTION</small>
          <h2>MOU Execution Desk</h2>
          <p>
            Approved documents move to PMC signature and then directly to PACS
            final execution.
          </p>
        </div>
        <div className="mou-desk-filters">
          {[
            ["ALL", "All"],
            ["PMC_PENDING", "Awaiting PMC Signature"],
            ["PACS", "Awaiting PACS Signature"],
            ["EXECUTED", "Fully Executed"],
          ].map(([value, label]) => (
            <button
              key={value}
              className={filter === value ? "active" : ""}
              onClick={() => setFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mou-kpis">
        <div>
          <span>Eligible MOU Projects</span>
          <b>{counts.total}</b>
        </div>
        <div>
          <span>PMC Signature Pending</span>
          <b>{counts.pmc}</b>
        </div>
        <div>
          <span>PACS Final Signature</span>
          <b>{counts.pacs}</b>
        </div>
        <div>
          <span>Fully Executed</span>
          <b>{counts.executed}</b>
        </div>
      </div>

      <div className="mou-desk-layout">
        <aside className="mou-project-list">
          {!visible.length && (
            <div className="mou-empty">
              No MOU project is available for this filter.
            </div>
          )}
          {visible.map((project) => {
            const status = statusOf(project);
            return (
              <button
                key={project.id}
                className={selectedId === project.id ? "selected" : ""}
                onClick={() => setSelectedId(project.id)}
              >
                <b>{getPacsName(project)}</b>
                <span>{project.applicationId || project.id}</span>
                <small>
                  {formatCapacity(getCapacity(project))} ·{" "}
                  {getDistrict(project) || "District pending"}
                </small>
                <em
                  className={status === "PENDING_PMC_SIGNATURE" ? "urgent" : ""}
                >
                  {{
                    PENDING_PMC_SIGNATURE: "AWAITING PMC SIGNATURE",
                    PENDING_PACS_SIGNATURE: "AWAITING PACS SIGNATURE",
                    FULLY_EXECUTED: "FULLY EXECUTED",
                  }[status] || status.replaceAll("_", " ")}
                </em>
              </button>
            );
          })}
        </aside>

        <div className="mou-workspace">
          {!selected && (
            <div className="mou-empty">Select a PACS MOU project.</div>
          )}
          {selected && (
              <PmcMouSigning
              key={selected.id}
                profile={profile}
                project={selected}
                onSaved={applyUpdate}
              />
            )}
          {selected && statusOf(selected) === "PENDING_PACS_SIGNATURE" && (
            <div className="mou-forwarded">
              <b>PMC Signature Completed</b>
              <span>
                The PMC-signed two-party MOU is available in the PACS portal
                for final authorised signatures and execution.
              </span>
              {(selected.pmcSignedMouUrl ||
                selected.mouSignatures?.pmc?.signedPdfUrl) && (
                <a
                  href={
                    selected.pmcSignedMouUrl ||
                    selected.mouSignatures?.pmc?.signedPdfUrl
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  View PMC Signed MOU
                </a>
              )}
            </div>
          )}
          {selected && statusOf(selected) === "FULLY_EXECUTED" && (
            <div className="mou-executed">
              <b>Final MOU Fully Executed</b>
              <span>PMC and PACS signatures are complete.</span>
              {getCurrentFinalMouUrl(selected) && (
                <a
                  href={
                    getCurrentFinalMouUrl(selected)
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  Download Final MOU
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

const MOU_DESK_STYLE = `
.mou-desk{color:#142b46;font-family:Inter,"Segoe UI",Arial,sans-serif}.mou-desk *{box-sizing:border-box}.mou-desk-head{display:flex;align-items:flex-end;justify-content:space-between;gap:20px;padding:20px;border:1px solid #d6e0ea;border-radius:15px;background:#fff}.mou-desk-head small{font-size:10px;font-weight:900;letter-spacing:.08em}.mou-desk-head h2{margin:5px 0;font-size:24px}.mou-desk-head p{margin:0;color:#64748b;font-size:12px}.mou-desk-filters{display:flex;flex-wrap:wrap;gap:7px}.mou-desk-filters button{padding:8px 10px;border:1px solid #c8d5e3;border-radius:8px;background:#fff;color:#36516d;font-size:10px;font-weight:800}.mou-desk-filters button.active{border-color:#0b315f;background:#0b315f;color:#fff}.mou-kpis{display:grid;grid-template-columns:repeat(5,1fr);gap:12px;margin:14px 0}.mou-kpis div{padding:15px;border:1px solid #d6e0ea;border-radius:12px;background:#fff}.mou-kpis span,.mou-kpis b{display:block}.mou-kpis span{color:#64748b;font-size:10px}.mou-kpis b{margin-top:5px;font-size:22px}.mou-desk-layout{display:grid;grid-template-columns:340px minmax(0,1fr);gap:14px}.mou-project-list,.mou-workspace{padding:12px;border:1px solid #d6e0ea;border-radius:13px;background:#fff}.mou-project-list button{display:block;width:100%;margin-bottom:8px;padding:12px;border:1px solid #d9e2ec;border-radius:9px;background:#fff;color:#233d58;text-align:left}.mou-project-list button.selected{border-color:#0b5cab;background:#f0f7ff}.mou-project-list b,.mou-project-list span,.mou-project-list small,.mou-project-list em{display:block}.mou-project-list span{margin-top:4px;color:#4f647a;font-size:10px}.mou-project-list small{margin-top:4px;color:#718096;font-size:9px}.mou-project-list em{width:max-content;margin-top:7px;padding:5px 7px;border-radius:20px;background:#edf2f7;color:#526579;font-size:8px;font-style:normal;font-weight:900}.mou-project-list em.urgent{background:#fff1dc;color:#9a5c00}.mou-empty,.mou-waiting,.mou-forwarded,.mou-executed{padding:25px;border:1px dashed #bdcbd9;border-radius:10px;text-align:center;color:#607287}.mou-waiting b,.mou-waiting span,.mou-forwarded b,.mou-forwarded span,.mou-executed b,.mou-executed span{display:block}.mou-waiting b,.mou-forwarded b,.mou-executed b{color:#183b5d;font-size:17px}.mou-waiting span,.mou-forwarded span,.mou-executed span{margin:6px 0 12px}.mou-forwarded{border-color:#b8d5ed;background:#f1f8fe}.mou-executed{border-color:#b8dfc5;background:#eff9f2}.mou-forwarded a,.mou-executed a{display:inline-block;padding:9px 11px;border-radius:8px;background:#0b315f;color:#fff;text-decoration:none;font-size:10px;font-weight:800}@media(max-width:1100px){.mou-kpis{grid-template-columns:repeat(3,1fr)}}@media(max-width:980px){.mou-desk-layout{grid-template-columns:1fr}.mou-kpis{grid-template-columns:repeat(2,1fr)}}@media(max-width:650px){.mou-desk-head{display:block}.mou-desk-filters{margin-top:13px}.mou-kpis{grid-template-columns:1fr}}
`;

function PmcRegistrarForwardingDesk({ projects = [], profile = {}, onProjectUpdated }) {
  const [selectedId, setSelectedId] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [response, setResponse] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const statusOf = (project = {}) =>
    String(
      project.registrarWorkflow?.pmc?.status ||
        project.pmcRegistrarStatus ||
        (project.registrarPackage?.url ? "WAITING_FOR_PMC_FORWARD" : "PACKAGE_PENDING"),
    ).toUpperCase();
  const districtOf = (project = {}) =>
    String(
      project.registrarWorkflow?.district?.status ||
        project.districtRegistrarStatus ||
        "WAITING_FOR_PMC_FORWARD",
    ).toUpperCase();
  const stateOf = (project = {}) =>
    String(
      project.registrarWorkflow?.state?.status ||
        project.stateRegistrarStatus ||
        "WAITING_FOR_DISTRICT_FORWARD",
    ).toUpperCase();
  const packageUrl = (project = {}) =>
    project.registrarPackage?.url ||
    project.registrarDocumentSnapshot?.package?.url ||
    project.registrarPackageUrl ||
    "";
  const hasBeenForwarded = (project = {}) =>
    project.pmcRegistrarFileForwardedToDistrict === true ||
    /FORWARDED_TO_DISTRICT|COMPLIANCE_RESUBMITTED/.test(statusOf(project));
  const workflowLabel = (project = {}) => {
    if (["APPROVED", "REJECTED"].includes(String(project.dcdcStatus || "").toUpperCase()))
      return `DCDC ${String(project.dcdcStatus).toUpperCase()}`;
    if (project.dcdcMeetingDate || project.dcdcStatus) return "DCDC IN PROCESS";
    if (/APPROVED_FOR_DCDC|PERMISSION_GRANTED/.test(stateOf(project))) return "STATE APPROVED · DCDC NEXT";
    if (/QUERY/.test(stateOf(project)) || /STATE_QUERY/.test(districtOf(project))) return "STATE QUERY CHAIN";
    if (/FORWARDED_TO_STATE/.test(districtOf(project))) return "WITH STATE REGISTRAR";
    if (/QUERY/.test(districtOf(project))) return "DISTRICT QUERY · PMC ACTION";
    if (hasBeenForwarded(project)) return "WITH DISTRICT REGISTRAR";
    if (packageUrl(project)) return "READY TO FORWARD";
    return "PACS PACKAGE PENDING";
  };

  const rows = projects.filter((project) => {
    const label = workflowLabel(project);
    if (filter === "ALL") return true;
    if (filter === "ACTION") return /READY TO FORWARD|PMC ACTION/.test(label) || statusOf(project).includes("STATE_QUERY_PENDING");
    if (filter === "DISTRICT") return /DISTRICT/.test(label);
    if (filter === "STATE") return /STATE/.test(label);
    return /DCDC/.test(label);
  });
  const selected = projects.find((project) => project.id === selectedId) || rows[0] || null;
  useEffect(() => {
    if (!selectedId && rows[0]?.id) setSelectedId(rows[0].id);
  }, [rows, selectedId]);

  const links = selected
    ? [
        ["Complete Registrar ZIP", packageUrl(selected)],
        ["PMC-signed Final DPR", selected.finalSignedDprUrl || selected.finalDprUrl || selected.dprOriginalPdfUrl || selected.dprPdfUrl],
        ["Executed PACS–PMC MOU", getCurrentFinalMouUrl(selected)],
        ["NAFED Assurance Letter", selected.nafedAssuranceLetter?.url || selected.assuranceLetter?.url || selected.nafedAssuranceLetterUrl],
      ].filter(([, url]) => url)
    : [];

  const save = async (kind) => {
    if (!selected?.id || !profile?.uid) return;
    const nowIso = new Date().toISOString();
    let action = "";
    let detail = "";
    let patch = {};
    if (kind === "INITIAL") {
      if (!packageUrl(selected)) return setMessage("PACS Registrar ZIP is not ready yet.");
      action = "PMC_REGISTRAR_FILE_FORWARDED_TO_DISTRICT";
      detail = `${getPacsName(selected)} complete Registrar file forwarded to District Registrar.`;
      patch = {
        pmcRegistrarFileForwardedToDistrict: true,
        pmcRegistrarStatus: "PMC_FILE_FORWARDED_TO_DISTRICT",
        registrarFileStage: "PMC_FORWARDED_TO_DISTRICT_REGISTRAR",
        registrarReviewStatus: "PENDING_DISTRICT_REGISTRAR",
        districtRegistrarStatus: "WAITING_FOR_DISTRICT_SCRUTINY",
        stateRegistrarStatus: "WAITING_FOR_DISTRICT_FORWARD",
        registrarWorkflowStage: "PMC_FILE_FORWARDED_TO_DISTRICT",
        nextResponsibleRole: "DISTRICT_REGISTRAR",
        pmcRegistrarForwardedAtIso: nowIso,
        pmcRegistrarForwardingRemarks: response.trim(),
        "registrarWorkflow.pmc.status": "PMC_FILE_FORWARDED_TO_DISTRICT",
        "registrarWorkflow.pmc.forwardedAtIso": nowIso,
        "registrarWorkflow.district.status": "WAITING_FOR_DISTRICT_SCRUTINY",
        "registrarWorkflow.state.status": "WAITING_FOR_DISTRICT_FORWARD",
      };
    } else if (kind === "DISTRICT_COMPLIANCE") {
      if (!response.trim()) return setMessage("Write the complete District-query response first.");
      action = "PMC_DISTRICT_QUERY_COMPLIANCE_SUBMITTED";
      detail = `${getPacsName(selected)} PMC compliance resubmitted to District Registrar.`;
      patch = {
        pmcRegistrarStatus: "PMC_COMPLIANCE_RESUBMITTED_TO_DISTRICT",
        districtRegistrarStatus: "PMC_COMPLIANCE_RESUBMITTED_TO_DISTRICT",
        districtRegistrarVerified: false,
        pmcRegistrarComplianceResponse: response.trim(),
        pmcRegistrarComplianceAtIso: nowIso,
        registrarWorkflowStage: "PMC_COMPLIANCE_RESUBMITTED_TO_DISTRICT",
        nextResponsibleRole: "DISTRICT_REGISTRAR",
        "registrarWorkflow.pmc.status": "PMC_COMPLIANCE_RESUBMITTED_TO_DISTRICT",
        "registrarWorkflow.district.status": "PMC_COMPLIANCE_RESUBMITTED_TO_DISTRICT",
      };
    } else {
      if (!response.trim()) return setMessage("Write the complete State-query response first.");
      action = "PMC_STATE_QUERY_COMPLIANCE_SUBMITTED";
      detail = `${getPacsName(selected)} PMC response to State query resubmitted to District Registrar.`;
      patch = {
        pmcRegistrarStatus: "PMC_STATE_QUERY_COMPLIANCE_RESUBMITTED_TO_DISTRICT",
        districtRegistrarStatus: "PMC_STATE_QUERY_COMPLIANCE_RESUBMITTED_TO_DISTRICT",
        pmcStateQueryComplianceResponse: response.trim(),
        pmcStateQueryComplianceAtIso: nowIso,
        registrarWorkflowStage: "PMC_STATE_QUERY_COMPLIANCE_RESUBMITTED_TO_DISTRICT",
        nextResponsibleRole: "DISTRICT_REGISTRAR",
        "registrarWorkflow.pmc.status": "PMC_STATE_QUERY_COMPLIANCE_RESUBMITTED_TO_DISTRICT",
        "registrarWorkflow.district.status": "PMC_STATE_QUERY_COMPLIANCE_RESUBMITTED_TO_DISTRICT",
      };
    }
    try {
      setBusy(true);
      setMessage("");
      await updateDoc(doc(db, NAFED_COLLECTIONS.godownProjects, selected.id), {
        ...patch,
        lastAuthorityAction: action,
        lastAuthorityActionByUid: profile.uid,
        lastAuthorityActionByRole: "PMC",
        updatedAt: serverTimestamp(),
      });
      onProjectUpdated?.(selected.id, patch);
      const auditWrites = await Promise.allSettled([
        addDoc(collection(db, NAFED_COLLECTIONS.activity), {
          action, entityType: "GODOWN_PROJECT", entityId: selected.id,
          applicationId: selected.applicationId || selected.projectId || selected.id,
          entityName: getPacsName(selected), state: getState(selected), district: getDistrict(selected),
          details: detail, performedByUid: profile.uid, performedByRole: "PMC", createdAt: serverTimestamp(),
        }),
        addDoc(collection(db, NAFED_COLLECTIONS.notifications), {
          type: action, title: "Registrar Workflow Updated", message: detail,
          projectId: selected.id, applicationId: selected.applicationId || selected.id,
          pacsName: getPacsName(selected), state: getState(selected), district: getDistrict(selected),
          pmcUid: profile.uid, targetRoles: ["PACS", "DISTRICT_REGISTRAR", "STATE_REGISTRAR"],
          isRead: false, createdAt: serverTimestamp(),
        }),
      ]);
      auditWrites.forEach((result) => result.status === "rejected" && console.error("Registrar secondary write failed", result.reason));
      setResponse("");
      setMessage("Registrar workflow updated successfully. All portals will receive the live status.");
    } catch (error) {
      console.error("PMC Registrar workflow update failed", error);
      setMessage(error?.code === "permission-denied" ? "PMC project update permission denied. Deploy the supplied Firestore Rules." : error.message || "Unable to update Registrar workflow.");
    } finally {
      setBusy(false);
    }
  };

  const needsDistrictResponse = selected && /DISTRICT_QUERY_RETURNED_TO_PMC|DISTRICT_REGISTRAR_QUERY/.test(districtOf(selected));
  const needsStateResponse = selected && statusOf(selected).includes("STATE_QUERY_PENDING_PMC_COMPLIANCE");
  return (
    <section className="reg-pmc-desk">
      <style>{`.reg-pmc-desk{color:#173653}.reg-head{display:flex;justify-content:space-between;gap:20px;padding:22px;border:1px solid #cad8e5;border-top:5px solid #187749;border-radius:15px;background:linear-gradient(110deg,#fff,#f2faf5)}.reg-head small{font-size:9px;font-weight:950;letter-spacing:.1em;color:#187749}.reg-head h2{margin:6px 0;font-size:25px}.reg-head p{margin:0;color:#65798e;font-size:11px}.reg-filters{display:flex;gap:7px;align-items:flex-end;flex-wrap:wrap}.reg-filters button{padding:9px 11px;border:1px solid #bccddd;border-radius:8px;background:#fff;color:#183f69;font-size:9px;font-weight:900}.reg-filters button.active{background:#113762;color:#fff}.reg-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:11px;margin:13px 0}.reg-kpis div{padding:14px;border:1px solid #d3dee9;border-radius:11px;background:#fff}.reg-kpis span,.reg-kpis b{display:block}.reg-kpis span{font-size:9px;color:#667b8f}.reg-kpis b{margin-top:5px;font-size:21px}.reg-layout{display:grid;grid-template-columns:330px minmax(0,1fr);gap:13px}.reg-list,.reg-work{padding:12px;border:1px solid #d3dee9;border-radius:13px;background:#fff}.reg-list button{width:100%;margin-bottom:8px;padding:12px;border:1px solid #d6e1ea;border-radius:9px;background:#fff;text-align:left;color:#173653}.reg-list button.selected{border-color:#19764a;background:#eef8f2}.reg-list b,.reg-list span,.reg-list small,.reg-list em{display:block}.reg-list span,.reg-list small{margin-top:4px;font-size:9px;color:#687c90}.reg-list em{width:max-content;margin-top:7px;padding:5px 7px;border-radius:20px;background:#fff0d5;color:#8d5700;font-size:8px;font-style:normal;font-weight:950}.reg-case-head{padding:16px;border-left:5px solid #123b68;background:#f5f8fb}.reg-case-head h3{margin:4px 0;font-size:20px}.reg-case-head p{margin:0;color:#667b8f;font-size:10px}.reg-status{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin:12px 0}.reg-status div{padding:11px;border:1px solid #d6e1ea;border-radius:8px}.reg-status span,.reg-status b{display:block}.reg-status span{font-size:8px;color:#6d8194}.reg-status b{margin-top:4px;font-size:10px;overflow-wrap:anywhere}.reg-docs{display:grid;grid-template-columns:repeat(2,1fr);gap:8px}.reg-docs a{padding:11px;border:1px solid #bfd0df;border-radius:8px;color:#174e82;text-decoration:none;font-size:10px;font-weight:900}.reg-query{margin-top:12px;padding:14px;border:1px solid #e6c57f;border-radius:9px;background:#fff8e8}.reg-query b,.reg-query span{display:block}.reg-query span{margin-top:5px;font-size:10px;color:#79540f}.reg-response{width:100%;min-height:90px;margin-top:12px;padding:11px;border:1px solid #b9cadb;border-radius:8px;resize:vertical}.reg-actions{display:flex;gap:8px;justify-content:flex-end;margin-top:10px}.reg-actions button{padding:10px 13px;border:0;border-radius:8px;background:#177647;color:#fff;font-size:9px;font-weight:950}.reg-actions button:disabled{opacity:.5}.reg-alert{margin:10px 0;padding:10px;border-radius:8px;background:#eef6ff;color:#174e82;font-size:10px}@media(max-width:980px){.reg-layout{grid-template-columns:1fr}.reg-kpis,.reg-status{grid-template-columns:repeat(2,1fr)}}@media(max-width:620px){.reg-head{display:block}.reg-filters{margin-top:12px}.reg-kpis,.reg-status,.reg-docs{grid-template-columns:1fr}}`}</style>
      <header className="reg-head"><div><small>PMC CONTROLLED REGISTRAR WORKFLOW</small><h2>Registrar File Forwarding & Compliance</h2><p>Forward PACS ZIP to District, answer queries and track State/DCDC live. MOU remains a separate two-party process.</p></div><div className="reg-filters">{["ALL","ACTION","DISTRICT","STATE","DCDC"].map((key)=><button key={key} className={filter===key?"active":""} onClick={()=>setFilter(key)}>{key}</button>)}</div></header>
      <div className="reg-kpis"><div><span>Allocated PACS Projects</span><b>{projects.length}</b></div><div><span>Packages Ready</span><b>{projects.filter((p)=>packageUrl(p)).length}</b></div><div><span>PMC Action Pending</span><b>{projects.filter((p)=>workflowLabel(p).includes("READY TO FORWARD")||workflowLabel(p).includes("PMC ACTION")).length}</b></div><div><span>State / DCDC</span><b>{projects.filter((p)=>/STATE|DCDC/.test(workflowLabel(p))).length}</b></div></div>
      {message && <div className="reg-alert">{message}</div>}
      <div className="reg-layout"><aside className="reg-list">{rows.map((project)=><button key={project.id} className={selected?.id===project.id?"selected":""} onClick={()=>{setSelectedId(project.id);setResponse("");setMessage("");}}><b>{getPacsName(project)}</b><span>{project.applicationId||project.projectId||project.id}</span><small>{formatCapacity(getCapacity(project))} · {getDistrict(project)||"District pending"}</small><em>{workflowLabel(project)}</em></button>)}</aside><div className="reg-work">{selected ? <><div className="reg-case-head"><small>OFFICIAL REGISTRAR FILE</small><h3>{getPacsName(selected)}</h3><p>{selected.applicationId||selected.projectId||selected.id} · {getDistrict(selected)} · {getState(selected)}</p></div><div className="reg-status"><div><span>PMC</span><b>{statusOf(selected).replaceAll("_"," ")}</b></div><div><span>District</span><b>{districtOf(selected).replaceAll("_"," ")}</b></div><div><span>State</span><b>{stateOf(selected).replaceAll("_"," ")}</b></div><div><span>DCDC</span><b>{String(selected.dcdcStatus||"NOT STARTED").replaceAll("_"," ")}</b></div></div><div className="reg-docs">{links.map(([label,url])=><a href={url} key={label} target="_blank" rel="noreferrer">{label} · Download</a>)}</div>{selected.districtRegistrarRemarks && <div className="reg-query"><b>District Registrar Query</b><span>{selected.districtRegistrarRemarks}</span></div>}{selected.stateRegistrarRemarks && <div className="reg-query"><b>State Registrar Query (via District)</b><span>{selected.stateRegistrarRemarks}</span></div>}{(needsDistrictResponse||needsStateResponse||(!hasBeenForwarded(selected)&&packageUrl(selected))) && <textarea className="reg-response" value={response} onChange={(event)=>setResponse(event.target.value)} placeholder={needsDistrictResponse||needsStateResponse?"Write complete compliance response...":"Optional PMC forwarding remarks..."}/>}<div className="reg-actions">{!hasBeenForwarded(selected)&&packageUrl(selected)&&<button disabled={busy} onClick={()=>save("INITIAL")}>Forward Complete File to District</button>}{needsDistrictResponse&&<button disabled={busy} onClick={()=>save("DISTRICT_COMPLIANCE")}>Submit Compliance to District</button>}{needsStateResponse&&<button disabled={busy} onClick={()=>save("STATE_COMPLIANCE")}>Submit State-query Response to District</button>}</div></> : <div className="empty-state"><h3>No project in this filter</h3><p>Select another filter or wait for a PACS Registrar package.</p></div>}</div></div>
    </section>
  );
}

export default function NafedPmcDashboard() {
  const [tab, setTab] = useState("home");
  const [userProfile, setUserProfile] = useState(null);
  const [allocation, setAllocation] = useState(null);
  const [allocationRecords, setAllocationRecords] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [pacsRegistrations, setPacsRegistrations] = useState([]);
  const [approvingPacsId, setApprovingPacsId] = useState("");
  const [loading, setLoading] = useState(true);
  const [welcomeOpen, setWelcomeOpen] = useState(true);
  const [liveSync, setLiveSync] = useState({
    connected: false,
    updatedAt: null,
  });

  const [summary, setSummary] = useState({
    districts: 0,
    projects: 0,
    pacs: 0,
    capacity: 0,
  });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      try {
        if (!u) {
          window.location.href = "/nafed";
          return;
        }

        const userSnap = await getDoc(doc(db, NAFED_COLLECTIONS.users, u.uid));

        if (!userSnap.exists()) {
          toast.error("NAFED user profile missing.");
          await signOut(auth);
          window.location.href = "/nafed";
          return;
        }

        const profile = {
          uid: u.uid,
          email: u.email,
          ...userSnap.data(),
        };

        const role = String(profile.role || "").trim().toUpperCase();
        const status = String(
          profile.status || profile.registrationStatus || "",
        )
          .trim()
          .toUpperCase();
        if (
          !["PMC", "PMC_AGENCY", "PMC AGENCY"].includes(role) ||
          status !== "APPROVED" ||
          profile.isActive === false ||
          isDeletedRecord(profile)
        ) {
          toast.error("PMC account is not approved.");
          await signOut(auth);
          window.location.href = "/nafed";
          return;
        }

        setUserProfile(profile);
      } catch (e) {
        console.error(e);
        toast.error("PMC dashboard loading failed.");
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    if (!userProfile?.uid) return undefined;

    let allUsers = [];
    let allProjects = [];
    let allAllocations = [];
    const ready = { users: false, projects: false, allocations: false };
    let stopped = false;

    const showRealtimeError = (label, error) => {
      console.error(`${label} realtime listener failed`, error);
      toast.error(`${label} live data could not be loaded.`);
      setLoading(false);
    };

    const rebuildScope = () => {
      if (stopped || !ready.users || !ready.projects || !ready.allocations) {
        return;
      }

      const pmcUid = userProfile.uid;
      const activeAllocations = allAllocations.filter(
        (item) =>
          isActiveAllocation(item) && getAllocatedPmcUid(item) === pmcUid,
      );
      const allocationKeys = new Set(
        activeAllocations.flatMap((item) => getPacsKeys(item)),
      );

      const registrations = allUsers
        .filter((item) => isPacsRole(item) && !isDeletedRecord(item))
        .filter(
          (item) =>
            getAllocatedPmcUid(item) === pmcUid ||
            matchesAnyKey(item, allocationKeys),
        )
        .sort((a, b) => getPacsName(a).localeCompare(getPacsName(b)));

      // A deleted PACS must remove all of its linked projects from every PMC view.
      const deletedPacsKeys = new Set(
        allUsers
          .filter((item) => isPacsRole(item) && isDeletedRecord(item))
          .flatMap((item) => getPacsKeys(item)),
      );
      const allowedPacsKeys = new Set(
        registrations.flatMap((item) => getPacsKeys(item)),
      );

      const assignedProjects = allProjects
        .filter(
          (project) =>
            isStrictNafedApplyProject(project) &&
            !isDeletedRecord(project) &&
            !matchesAnyKey(project, deletedPacsKeys),
        )
        .filter((project) => {
          const projectPmc = getAllocatedPmcUid(project);
          if (projectPmc) return projectPmc === pmcUid;
          return matchesAnyKey(project, allowedPacsKeys);
        })
        .sort((a, b) => {
          const aTime = a.updatedAt?.seconds || a.createdAt?.seconds || 0;
          const bTime = b.updatedAt?.seconds || b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

      const assignedDistricts = [
        ...new Set(
          [...registrations, ...assignedProjects]
            .map(getDistrict)
            .filter(Boolean),
        ),
      ].sort();
      const assignedStates = [
        ...new Set(
          [...registrations, ...assignedProjects].map(getState).filter(Boolean),
        ),
      ];

      const allocationScope = registrations.length
        ? {
            id: "PACS_WISE_ASSIGNMENT",
            allocationMode: "PACS_WISE",
            pmcUid,
            pmcName:
              userProfile.organizationName ||
              userProfile.agencyName ||
              userProfile.name ||
              "PMC Agency",
            state: userProfile.state || assignedStates[0] || "",
            states: assignedStates,
            districts: assignedDistricts,
            pacsIds: registrations.map((item) => item.id),
            allocationIds: activeAllocations.map((item) => item.id),
          }
        : null;

      setAllocationRecords(activeAllocations);
      setPacsRegistrations(registrations);
      setMyProjects(assignedProjects);
      setAllocation(allocationScope);
      setSummary({
        districts: assignedDistricts.length,
        projects: assignedProjects.length,
        pacs: registrations.length,
        capacity: assignedProjects.reduce(
          (total, project) => total + getCapacity(project),
          0,
        ),
      });
      setLiveSync({ connected: true, updatedAt: new Date() });
      setLoading(false);
    };

    const unsubscribeUsers = onSnapshot(
      collection(db, NAFED_COLLECTIONS.users),
      (snapshot) => {
        allUsers = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));
        ready.users = true;
        rebuildScope();
      },
      (error) => showRealtimeError("PACS", error),
    );

    const unsubscribeProjects = onSnapshot(
      collection(db, NAFED_COLLECTIONS.godownProjects),
      (snapshot) => {
        allProjects = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));
        ready.projects = true;
        rebuildScope();
      },
      (error) => showRealtimeError("Projects", error),
    );

    const unsubscribeAllocations = onSnapshot(
      collection(db, NAFED_COLLECTIONS.pacsPmcAllocations),
      (snapshot) => {
        allAllocations = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));
        ready.allocations = true;
        rebuildScope();
      },
      (error) => showRealtimeError("Allocations", error),
    );

    return () => {
      stopped = true;
      unsubscribeUsers();
      unsubscribeProjects();
      unsubscribeAllocations();
    };
  }, [userProfile]);

  const buildScope = () => {
    const districtList = allocation?.districts || [];
    const projectIds = myProjects.map((p) => p.id);

    return {
      nafedMode: true,
      onlyNafedApply: true,
      strictNafedApplyOnly: true,

      nafedProject: true,
      projectSource: "NAFED",
      applicationPortal: "NAFED_PACS_APPLY",

      collectionName: NAFED_COLLECTIONS.godownProjects,
      state: allocation?.state || "",
      districts: districtList,
      allowedDistricts: districtList,

      pmcEmail: userProfile?.email || "",
      pmcUid: userProfile?.uid || "",

      assignedProjectIds: projectIds,
      allowedProjectIds: projectIds,
      assignedProjects: myProjects,
      projects: myProjects,
      pacsRegistrations,
      allocatedPacsIds: pacsRegistrations.map((item) => item.id),

      // Children must use the exact project list prepared from PACS-wise allocation.
      filterProject: (p) => {
        const projectId = String(p?.id || p?.projectId || "").trim();
        return (
          isStrictNafedApplyProject(p) &&
          !isDeletedRecord(p) &&
          projectIds.includes(projectId)
        );
      },
    };
  };

  const approvePacsRegistration = async (registration) => {
    if (!registration?.id || !userProfile?.uid) return;
    if (approvingPacsId) return;
    const approval = window.confirm(
      `Approve ${getPacsName(registration)} and allow PACS portal work to start?`,
    );
    if (!approval) return;

    setApprovingPacsId(registration.id);
    try {
      const currentSnap = await getDoc(
        doc(db, NAFED_COLLECTIONS.users, registration.id),
      );
      if (!currentSnap.exists() || isDeletedRecord(currentSnap.data())) {
        toast.error("This PACS registration is no longer active.");
        return;
      }

      const currentRegistration = {
        id: currentSnap.id,
        ...currentSnap.data(),
      };
      // Approval must match a stable PACS id/email/registration key, never name only.
      const currentKeys = new Set(getPacsKeys(currentRegistration, false));
      const directlyAllocated =
        getAllocatedPmcUid(currentRegistration) === userProfile.uid;
      const matchedAllocation = allocationRecords.find(
        (item) =>
          isActiveAllocation(item) &&
          getAllocatedPmcUid(item) === userProfile.uid &&
          getPacsKeys(item, false).some((key) => currentKeys.has(key)),
      );
      const hasLiveAllocation = directlyAllocated || Boolean(matchedAllocation);

      if (!hasLiveAllocation) {
        toast.error(
          "State Branch allocation changed. This PACS is not assigned to your PMC.",
        );
        return;
      }

      /*
       * Security-rule bridge for legacy PACS-wise allocations.
       *
       * Newer records mirror allocatedPmcUid directly on nafed_users. Older
       * records may exist only in pacs_pmc_allocations. The allocation document
       * id is stored with the approval so Firestore Rules can verify that exact
       * active allocation without granting one PMC access to another PMC's PACS.
       */
      const approvedAt = serverTimestamp();
      const approvalUpdate = {
        status: "APPROVED",
        isActive: true,
        workStartApproved: true,
        workStartStatus: "APPROVED",
        approvedByPmcUid: userProfile.uid,
        approvedByPmcEmail: userProfile.email || "",
        approvedAt,
        updatedAt: approvedAt,
      };

      if (matchedAllocation?.id) {
        approvalUpdate.approvalAllocationId = matchedAllocation.id;
      }

      /*
       * Commit the access approval first. Notification and audit are useful
       * secondary records, but a legacy rule on either collection must never
       * roll back an otherwise authorised PACS approval.
       */
      await updateDoc(
        doc(db, NAFED_COLLECTIONS.users, registration.id),
        approvalUpdate,
      );

      try {
        await addDoc(collection(db, NAFED_COLLECTIONS.notifications), {
          type: "PACS_WORK_START_APPROVED",
          title: "PACS Registration Approved",
          message: `${getPacsName(registration)} has been approved by the allocated PMC and portal access is active.`,
          pacsUid: registration.id,
          pacsEmail: registration.email || "",
          pacsName: getPacsName(registration),
          state: registration.state || allocation?.state || "",
          district: registration.district || "",
          pmcUid: userProfile.uid,
          pmcName:
            userProfile.organizationName || userProfile.name || "PMC Agency",
          visibleTo: ["PACS", "PMC", "STATE_BRANCH", "HEAD_OFFICE"],
          readBy: [],
          status: "ACTIVE",
          createdAt: serverTimestamp(),
        });
      } catch (notificationError) {
        console.error(
          "PACS approved, but nafed_notifications write failed",
          notificationError,
        );
      }

      try {
        await addDoc(collection(db, NAFED_COLLECTIONS.activity), {
          action: "PACS_WORK_START_APPROVED",
          entityType: "PACS_REGISTRATION",
          entityId: registration.id,
          entityName: getPacsName(registration),
          state: registration.state || allocation?.state || "",
          district: registration.district || "",
          performedByUid: userProfile.uid,
          performedByEmail: userProfile.email || "",
          performedByRole: "PMC",
          headOfficeVisible: true,
          stateBranchVisible: true,
          createdAt: serverTimestamp(),
        });
      } catch (activityError) {
        console.error(
          "PACS approved, but nafed_activity write failed",
          activityError,
        );
      }

      let emailQueued = false;
      if (registration.email) {
        try {
          await addDoc(collection(db, NAFED_COLLECTIONS.mailQueue), {
            to: [registration.email],
            message: {
              subject:
                "NAFED PACS Registration Approved - Portal Access Activated",
              text: `Dear ${registration.name || "PACS Administrator"}, your PACS registration for ${getPacsName(registration)} has been approved. You can now login to the NAFED PACS Portal and submit a new grain storage application.`,
              html: `<div style="font-family:Arial,sans-serif;max-width:640px;margin:auto;border:1px solid #dbe5df;border-radius:14px;overflow:hidden"><div style="background:#075b3d;color:white;padding:22px"><h2 style="margin:0">NAFED PACS Portal</h2><p style="margin:6px 0 0">Registration Approval Notification</p></div><div style="padding:24px;color:#18352b"><p>Dear ${registration.name || "PACS Administrator"},</p><p>Your PACS registration for <strong>${getPacsName(registration)}</strong> has been approved by the allocated PMC.</p><table style="width:100%;border-collapse:collapse;margin:18px 0"><tr><td style="padding:9px;border:1px solid #dde7e1">Registration Number</td><td style="padding:9px;border:1px solid #dde7e1"><strong>${registration.registrationNumber || registration.pacsRegistrationNo || "Not available"}</strong></td></tr><tr><td style="padding:9px;border:1px solid #dde7e1">District</td><td style="padding:9px;border:1px solid #dde7e1"><strong>${registration.district || "-"}</strong></td></tr><tr><td style="padding:9px;border:1px solid #dde7e1">Access Status</td><td style="padding:9px;border:1px solid #dde7e1;color:#08713f"><strong>ACTIVE</strong></td></tr></table><p>You can now login to the NAFED PACS Portal and submit a new grain storage application.</p><p>Regards,<br><strong>NAFED Digital Project Monitoring System</strong></p></div></div>`,
            },
            deliveryStatus: "QUEUED",
            module: "PACS_REGISTRATION_APPROVAL",
            createdAt: serverTimestamp(),
          });
          emailQueued = true;
        } catch (mailError) {
          // Email queue must never roll back an already completed approval.
          console.error("PACS approval email queue failed", mailError);
        }
      }

      setPacsRegistrations((current) =>
        current.map((item) =>
          item.id === registration.id
            ? {
                ...item,
                status: "APPROVED",
                isActive: true,
                workStartApproved: true,
                workStartStatus: "APPROVED",
                approvalAllocationId:
                  matchedAllocation?.id || item.approvalAllocationId || "",
              }
            : item,
        ),
      );

      toast.success(
        registration.email && !emailQueued
          ? "PACS approved. Email queue needs attention."
          : "PACS registration approved. Portal access is now active.",
      );
    } catch (error) {
      console.error("nafed_users PACS approval update failed", {
        code: error?.code,
        message: error?.message,
        pacsUid: registration.id,
        pmcUid: userProfile.uid,
      });
      toast.error(
        error?.code === "permission-denied"
          ? "PACS profile update permission denied. Publish the supplied final Firestore Rules and try again."
          : "PACS registration approval failed.",
      );
    } finally {
      setApprovingPacsId("");
    }
  };


  const activeModule = useMemo(() => {
    return MODULES.find((m) => m.key === tab);
  }, [tab]);

  const workflowSummary = useMemo(() => {
    const hasDpr = (project = {}) =>
      Boolean(
        project.finalSignedDprUrl ||
          project.finalDprUrl ||
          project.dprOriginalPdfUrl ||
          project.dprPdfUrl ||
          project.dprUrl ||
          project.pmcVerificationDocuments?.PMC_FINAL_DPR?.url ||
          project.pmcTechnicalDocuments?.PMC_FINAL_DPR?.url,
      );
    return {
      documents: myProjects.filter(isPmcDocumentSetApproved).length,
      mou: myProjects.filter(isOfficialMouExecuted).length,
      dpr: myProjects.filter(hasDpr).length,
      assurance: myProjects.filter((project) =>
        Boolean(getNafedAssuranceLetter(project)),
      ).length,
      registrar: myProjects.filter((project) =>
        Boolean(
          project.pmcRegistrarFileForwardedToDistrict ||
            /FORWARDED_TO_DISTRICT|COMPLIANCE_RESUBMITTED/.test(
              String(project.pmcRegistrarStatus || "").toUpperCase(),
            ),
        ),
      ).length,
    };
  }, [myProjects]);

  const renderModule = () => {
    const scope = buildScope();

    if (!allocation) {
      return (
        <div className="empty-state">
          <h2>No PACS Allocation Found</h2>
          <p>
            Module access will be enabled after the State Branch allocates at
            least one PACS to this PMC.
          </p>
        </div>
      );
    }

    if (tab === "registrationApproval")
      return (
        <PacsRegistrationApprovalPane
          records={pacsRegistrations}
          onApprove={approvePacsRegistration}
          approvingPacsId={approvingPacsId}
        />
      );
    if (tab === "pacs") return <PacsApplicationsPane nafedScope={scope} />;
    if (tab === "visit") return <FieldVisitPane nafedScope={scope} />;
    if (tab === "pmcapproval") return <ApprovalDeskPane nafedScope={scope} />;
    if (tab === "pacsDocumentScrutiny")
      return (
        <PmcPacsDocumentScrutiny
          scope={scope}
          profile={userProfile || {}}
          onProjectUpdated={(projectId, changes) =>
            setMyProjects((current) =>
              current.map((project) =>
                project.id === projectId ? { ...project, ...changes } : project,
              ),
            )
          }
          onOpenMou={() => setTab("mouExecution")}
        />
      );
    if (tab === "mouExecution") {
      return (
        <PmcMouExecutionDesk
          projects={myProjects}
          profile={userProfile || {}}
          onProjectUpdated={(projectId, changes) =>
            setMyProjects((current) =>
              current.map((project) =>
                project.id === projectId ? { ...project, ...changes } : project,
              ),
            )
          }
        />
      );
    }

    if (tab === "technicalVendors") {
      return (
        <NafedPmcWorkflowPanel
          nafedScope={scope}
          scope={scope}
          projects={myProjects}
        />
      );
    }

    if (tab === "dprGenerator") {
      return (
        <NafedDprGenerator
          projects={myProjects}
          profile={userProfile || {}}
          onProjectUpdated={(projectId, changes) =>
            setMyProjects((current) =>
              current.map((project) =>
                project.id === projectId
                  ? { ...project, ...changes }
                  : project,
              ),
            )
          }
        />
      );
    }

    if (tab === "assuranceLetter") {
      return (
        <PmcAssuranceLetterDesk
          projects={myProjects}
          profile={userProfile || {}}
          onProjectUpdated={(projectId, changes) =>
            setMyProjects((current) =>
              current.map((project) =>
                project.id === projectId
                  ? { ...project, ...changes }
                  : project,
              ),
            )
          }
        />
      );
    }

    if (tab === "registrarForwarding") {
      return (
        <PmcRegistrarForwardingDesk
          projects={myProjects}
          profile={userProfile || {}}
          onProjectUpdated={(projectId, changes) =>
            setMyProjects((current) =>
              current.map((project) =>
                project.id === projectId ? { ...project, ...changes } : project,
              ),
            )
          }
        />
      );
    }

    if (tab === "nafedTender")
      return (
        <NafedTenderPublisherPane
          projects={myProjects}
          profile={userProfile || {}}
          onPublished={(projectId, tenderId) =>
            setMyProjects((current) =>
              current.map((project) =>
                project.id === projectId
                  ? {
                      ...project,
                      tenderId,
                      generatedTenderId: tenderId,
                      tenderStatus: "LIVE",
                      currentStage: "Tender Live - All India Portal",
                    }
                  : project,
              ),
            )
          }
        />
      );
    if (tab === "boq") return <PmcBoqSetupPane nafedScope={scope} />;

    if (tab === "measSetup") {
      return <PmcEngineerMeasurementSetupPane nafedScope={scope} />;
    }

    if (tab === "dailyProgress") {
      return <PmcDailyProgressReport nafedScope={scope} />;
    }

    if (tab === "generateLoa") return <GenerateLOA nafedScope={scope} />;

    if (tab === "loa") {
      return <PmcLoaWorkOrderPane nafedScope={scope} />;
    }

    if (tab === "contractorDocs") {
      return <PmcContractorDocumentsPane nafedScope={scope} />;
    }

    return null;
  };

  const doLogout = async () => {
    await signOut(auth);
    window.location.href = "/nafed";
  };

  if (loading) {
    return <LoaderScreen />;
  }
  return (
    <>
      <Toaster position="top-center" />

      <style>{`
        body {
          margin: 0;
          font-family: Inter, Segoe UI, Arial, sans-serif;
          background: #edf2f6;
        }

        .pmc-page {
          min-height: 100vh;
          position: relative;
          overflow-x: hidden;
          background:
            linear-gradient(180deg, #f8fafc 0, #edf3f7 48%, #e7eef3 100%);
          padding: 18px 0 34px;
        }

        .pmc-page::before {
          content: "";
          position: fixed;
          inset: 0;
          pointer-events: none;
          background-image: linear-gradient(rgba(12,55,94,.025) 1px, transparent 1px), linear-gradient(90deg, rgba(12,55,94,.025) 1px, transparent 1px);
          background-size: 42px 42px;
        }

        .pmc-page::after {
          content: "";
          position: fixed;
          display: none;
        }

        @keyframes bgmove {
          to { transform: rotate(360deg); }
        }

        .pmc-wrap {
          width: min(1520px, calc(100% - 36px));
          margin: 0 auto;
          position: relative;
          z-index: 2;
        }

        .pmc-header {
          min-height: 82px;
          position: relative;
          overflow: hidden;
          border-radius: 16px;
          background: #ffffff;
          color: #102f55;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 20px;
          box-shadow: 0 15px 40px rgba(15,39,65,.12);
          border: 1px solid #cbd8e5;
        }

        .pmc-header::before {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          left: 0;
          height: 4px;
          background: linear-gradient(90deg,#e89a2c 0 33.33%,#f8fafc 33.33% 66.66%,#20824d 66.66%);
        }

        .pmc-header > * {
          position: relative;
          z-index: 1;
        }

        .brand {
          display: flex;
          align-items: center;
          gap: 16px;
          min-width: 0;
        }

        .nafed-logo {
          width: 155px;
          height: 82px;
          background: #ffffff;
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 8px;
          overflow: hidden;
          box-shadow:
            0 10px 25px rgba(0,0,0,.18),
            inset 0 0 0 1px rgba(0,0,0,.04);
          flex-shrink: 0;
        }

        .nafed-logo-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          display: block;
        }

        .brand h1 {
          margin: 0;
          font-size: 25px;
          font-weight: 950;
          color: #102f55;
          letter-spacing: -.02em;
          white-space: nowrap;
        }

        .brand p {
          margin: 4px 0 0;
          font-size: 12px;
          color: #4e657d;
          font-weight: 800;
          white-space: nowrap;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 16px;
          flex: 0 0 auto;
        }

        .head-info {
          display: flex;
          align-items: center;
          gap: 9px;
          font-size: 11px;
          color: #526a82;
          font-weight: 800;
        }

        .hicon {
          width: 31px;
          height: 31px;
          border-radius: 11px;
          display: grid;
          place-items: center;
          background: #edf3f8;
          color: #164a77;
          font-weight: 950;
        }

        .head-info b {
          display: block;
          color: #102f55;
          font-size: 12px;
          margin-bottom: 2px;
        }

        .live-sync {
          display: flex;
          align-items: center;
          gap: 7px;
          padding: 8px 10px;
          border: 1px solid #a9d5b6;
          border-radius: 12px;
          background: #edf8f0;
          color: #17683f;
          font-size: 10px;
          font-weight: 900;
          white-space: nowrap;
        }

        .live-dot {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: #4ade80;
          box-shadow: 0 0 0 4px rgba(74,222,128,.14);
          animation: livePulse 1.8s ease-in-out infinite;
        }

        @keyframes livePulse {
          50% { opacity: .45; transform: scale(.82); }
        }

        .avatar {
          width: 38px;
          height: 38px;
          border-radius: 999px;
          background: #e8f3eb;
          color: #064e3b;
          display: grid;
          place-items: center;
          font-weight: 950;
          border: 1px solid #acd0b5;
          box-shadow: 0 8px 18px rgba(15,39,65,.09);
        }

        .logout {
          border: 0;
          background: #123a69;
          color: white;
          border-radius: 12px;
          padding: 9px 12px;
          font-weight: 950;
          cursor: pointer;
        }

        .logout:hover {
          background: #0a2b51;
        }

        .pmc-body {
          padding: 26px 0 0;
        }

        .welcome-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 22px;
          margin-bottom: 22px;
        }

        .welcome h2 {
          margin: 0;
          color: #102f55;
          text-shadow: none;
          font-size: 30px;
          font-weight: 950;
        }

        .welcome p {
          margin: 8px 0 0;
          color: #526a82;
          text-shadow: none;
          font-size: 14px;
          font-weight: 800;
        }

        .stats-card {
          background: #fff;
          border: 1px solid #cedae6;
          border-radius: 14px;
          box-shadow: 0 12px 32px rgba(15,39,65,.09);
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          min-width: 620px;
          overflow: hidden;
          backdrop-filter: blur(16px);
        }

        .stat {
          padding: 16px 20px;
          display: flex;
          gap: 12px;
          align-items: center;
          border-right: 1px solid #e2e8f0;
        }

        .stat:last-child {
          border-right: 0;
        }

        .stat-icon {
          width: 38px;
          height: 38px;
          border-radius: 999px;
          display: grid;
          place-items: center;
          font-weight: 950;
          font-size: 12px;
        }

        .stat label {
          display: block;
          color: #64748b;
          font-size: 11px;
          font-weight: 950;
        }

        .stat b {
          display: block;
          margin-top: 3px;
          color: #071f45;
          font-size: 24px;
          font-weight: 950;
        }

        .home-card {
          background: #fff;
          border: 1px solid #ccd9e5;
          border-radius: 16px;
          box-shadow: 0 16px 42px rgba(15,39,65,.1);
          padding: 22px;
          backdrop-filter: blur(18px);
          position: relative;
          overflow: hidden;
        }

        .section-title {
          border-left: 4px solid #16a34a;
          padding-left: 14px;
          margin-bottom: 18px;
        }

        .section-title h3 {
          margin: 0;
          font-size: 23px;
          color: #071f45;
          font-weight: 950;
        }

        .section-title p {
          margin: 6px 0 0;
          color: #475569;
          font-size: 12px;
          font-weight: 800;
        }

        .module-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(295px, 1fr));
          gap: 15px;
        }

        .module-card {
          border: 1px solid #dbe4ef;
          border-radius: 12px;
          background: #fff;
          min-height: 112px;
          padding: 17px;
          display: grid;
          grid-template-columns: 62px 1fr 32px;
          align-items: center;
          gap: 15px;
          cursor: pointer;
          transition: .22s ease;
          box-shadow: 0 7px 18px rgba(15,39,65,.055);
          position: relative;
          overflow: hidden;
        }

        .module-card::after {
          content: "";
          position: absolute;
          width: 120px;
          height: 120px;
          right: -70px;
          top: -70px;
          border-radius: 50%;
          background: rgba(22,163,74,.07);
          transition: .25s ease;
        }

        .module-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 22px 46px rgba(15,23,42,.18);
          border-color: #86efac;
        }

        .module-card:hover::after {
          transform: scale(1.45);
          background: rgba(22,163,74,.13);
        }

        .module-card:active {
          transform: scale(.985);
        }

        .module-icon {
          width: 58px;
          height: 58px;
          border-radius: 17px;
          display: grid;
          place-items: center;
          font-size: 27px;
          font-weight: 950;
          position: relative;
          z-index: 1;
        }

        .module-icon.green { background: #dcfce7; color: #15803d; }
        .module-icon.orange { background: #ffedd5; color: #ea580c; }
        .module-icon.blue { background: #dbeafe; color: #2563eb; }
        .module-icon.purple { background: #f3e8ff; color: #9333ea; }
        .module-icon.yellow { background: #fef3c7; color: #ca8a04; }
        .module-icon.teal { background: #ccfbf1; color: #0f766e; }
        .module-icon.pink { background: #fce7f3; color: #db2777; }

        .module-card h4 {
          margin: 3px 0 0;
          color: #11385f;
          font-size: 16px;
          font-weight: 950;
        }

        .module-card p {
          margin: 7px 0 0;
          color: #334155;
          font-size: 12.5px;
          line-height: 1.45;
          font-weight: 750;
        }

        .module-label {
          display: block;
          color: #247049;
          font-size: 8px;
          font-weight: 950;
          letter-spacing: .07em;
        }

        .workflow-command {
          margin-bottom: 18px;
          overflow: hidden;
          border: 1px solid #cbd8e5;
          border-radius: 16px;
          background: #fff;
          box-shadow: 0 14px 36px rgba(15,39,65,.09);
        }

        .workflow-command-title {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          padding: 18px 20px;
          border-bottom: 1px solid #d7e1ea;
          background: linear-gradient(110deg,#102f58,#0d6347);
          color: #fff;
        }

        .workflow-command-title small {
          color: #a8edc1;
          font-size: 9px;
          font-weight: 950;
          letter-spacing: .1em;
        }

        .workflow-command-title h3 {
          margin: 4px 0;
          font-size: 20px;
        }

        .workflow-command-title p {
          margin: 0;
          color: #d9ede4;
          font-size: 11px;
        }

        .workflow-command-title > span {
          padding: 8px 11px;
          border: 1px solid rgba(255,255,255,.3);
          border-radius: 999px;
          background: rgba(255,255,255,.1);
          font-size: 9px;
          font-weight: 950;
          white-space: nowrap;
        }

        .workflow-command-grid {
          display: grid;
          grid-template-columns: repeat(5,1fr);
        }

        .workflow-command-grid button {
          display: grid;
          grid-template-columns: 36px 1fr auto;
          align-items: center;
          gap: 10px;
          padding: 15px;
          border: 0;
          border-right: 1px solid #dbe4ec;
          background: #fff;
          color: #173653;
          text-align: left;
          cursor: pointer;
        }

        .workflow-command-grid button:last-child { border-right: 0; }
        .workflow-command-grid button:hover { background: #f0f7f3; }
        .workflow-command-grid button > span {
          display: grid;
          width: 34px;
          height: 34px;
          place-items: center;
          border-radius: 50%;
          background: #e7f1fb;
          color: #174c7d;
          font-size: 9px;
          font-weight: 950;
        }

        .workflow-command-grid small,.workflow-command-grid b { display:block; }
        .workflow-command-grid small { color:#6b7e91;font-size:9px;font-weight:850; }
        .workflow-command-grid b { margin-top:3px;font-size:15px; }
        .workflow-command-grid em { color:#197149;font-size:9px;font-style:normal;font-weight:950; }

        .arrow {
          width: 31px;
          height: 31px;
          border-radius: 999px;
          background: #eef8f0;
          color: #15803d;
          display: grid;
          place-items: center;
          font-size: 20px;
          font-weight: 950;
          position: relative;
          z-index: 1;
        }
                  .module-page {
          background: rgba(255,255,255,.97);
          border: 1px solid rgba(255,255,255,.62);
          border-radius: 24px;
          box-shadow: 0 28px 78px rgba(2,6,23,.28);
          overflow: hidden;
          backdrop-filter: blur(18px);
        }

        .module-head {
          padding: 16px 20px;
          background: linear-gradient(
            105deg,
            rgba(6,36,74,.98),
            rgba(6,78,59,.98)
          );
          color: white;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }

        .module-head h2 {
          margin: 0;
          font-size: 22px;
          font-weight: 950;
        }

        .module-head p {
          margin: 4px 0 0;
          color: #d1fae5;
          font-size: 12px;
          font-weight: 750;
        }

        .back-btn {
          border: 0;
          background: white;
          color: #064e3b;
          border-radius: 12px;
          padding: 10px 14px;
          font-weight: 950;
          cursor: pointer;
          box-shadow: 0 10px 24px rgba(0,0,0,.14);
        }

        .back-btn:hover {
          background: #ecfdf5;
        }

        .module-content {
          padding: 16px;
          background: #f8fafc;
        }

        .empty-state {
          border: 1.5px dashed #cbd5e1;
          background: #f8fafc;
          border-radius: 18px;
          padding: 40px;
          text-align: center;
          color: #475569;
        }

        .empty-state h2 {
          margin: 0 0 8px;
          color: #071f45;
          font-weight: 950;
        }

        .empty-state p {
          margin: 0;
          font-weight: 750;
        }

.registration-desk{padding:4px}.registration-head{display:flex;justify-content:space-between;gap:20px;align-items:flex-end;padding:20px;border-radius:18px;background:linear-gradient(120deg,#062b55,#0b6b55);color:#fff}.registration-head small{color:#86efac;font-weight:950;letter-spacing:.1em}.registration-head h2{margin:6px 0;font-size:25px}.registration-head p{margin:0;color:#d1fae5}.registration-filters{display:flex;gap:7px}.registration-filters button{border:1px solid rgba(255,255,255,.3);background:rgba(255,255,255,.12);color:#fff;border-radius:999px;padding:8px 12px;font-size:10px;font-weight:900;cursor:pointer}.registration-filters button.active{background:#fff;color:#07553e}.registration-summary{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin:14px 0}.registration-summary div{background:#fff;border:1px solid #dce6ef;border-radius:14px;padding:15px;box-shadow:0 8px 20px rgba(15,23,42,.05)}.registration-summary span,.registration-summary b{display:block}.registration-summary span{color:#64748b;font-size:11px}.registration-summary b{color:#092f62;font-size:24px;margin-top:5px}.registration-table-wrap{background:#fff;border:1px solid #dce6ef;border-radius:16px;overflow:auto;box-shadow:0 12px 30px rgba(15,23,42,.07)}.registration-table{width:100%;min-width:1050px;border-collapse:collapse;text-align:left}.registration-table th{padding:13px 14px;background:#eef4f9;color:#53677e;font-size:10px;text-transform:uppercase;letter-spacing:.05em;border-bottom:1px solid #dce6ef}.registration-table td{padding:14px;border-bottom:1px solid #e5ebf1;vertical-align:middle;color:#243a54;font-size:11px}.registration-table tbody tr:hover{background:#f8fbfd}.registration-table tbody tr:last-child td{border-bottom:0}.registration-table td>b,.registration-table td>span{display:block}.registration-table td>span{color:#718096;font-size:9px;margin-top:4px}.pacs-cell{display:flex;align-items:center;gap:10px;min-width:210px}.pacs-cell b,.pacs-cell span{display:block}.pacs-cell span{color:#718096;font-size:9px;margin-top:4px}.pacs-mark{width:42px;height:42px;flex:0 0 42px;border-radius:12px;background:linear-gradient(135deg,#dcfce7,#dbeafe);color:#08713f;display:grid;place-items:center;font-weight:950}.table-status{width:max-content;border-radius:999px;padding:7px 9px!important;font-size:8px!important;font-weight:950}.table-status.pending{background:#fff4df;color:#9a5b00!important}.table-status.approved{background:#e7f8ec;color:#08713f!important}.approve-work{border:0;border-radius:9px;background:linear-gradient(135deg,#15803d,#22a44d);color:#fff;padding:10px 12px;font-size:10px;font-weight:950;cursor:pointer;white-space:nowrap;box-shadow:0 7px 16px rgba(21,128,61,.2)}.approve-work:disabled{cursor:wait;opacity:.68;box-shadow:none}.access-active{background:#edf9f0;border:1px solid #b9e0c4;border-radius:9px;padding:9px}.access-active b,.access-active span{display:block}.access-active b{color:#08713f}.access-active span{color:#527060;font-size:8px;margin-top:3px}

        .welcome-pop {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: grid;
          place-items: center;
          background: rgba(2, 6, 23, 0.52);
          backdrop-filter: blur(8px);
          padding: 18px;
          animation: fadeIn .25s ease;
        }

        .welcome-card {
          width: min(560px, 100%);
          background:
            radial-gradient(circle at top left, rgba(34,197,94,.18), transparent 32%),
            linear-gradient(135deg, rgba(255,255,255,.98), rgba(240,253,244,.96));
          border: 1px solid rgba(255,255,255,.75);
          border-radius: 26px;
          padding: 26px;
          box-shadow: 0 34px 100px rgba(2,6,23,.38);
          animation: popUp .28s ease;
        }

        .welcome-logo {
          width: 155px;
          height: 82px;
          background: #fff;
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 8px;
          overflow: hidden;
          box-shadow:
            0 10px 25px rgba(0,0,0,.18),
            inset 0 0 0 1px rgba(0,0,0,.04);
          margin-bottom: 16px;
        }

        .welcome-logo img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          display: block;
        }

        .welcome-card h2 {
          margin: 0;
          color: #071f45;
          font-size: 28px;
          font-weight: 950;
        }

        .welcome-card p {
          margin: 9px 0 0;
          color: #475569;
          font-size: 14px;
          line-height: 1.7;
          font-weight: 750;
        }

        .welcome-grid {
          margin: 16px 0;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
        }

        .welcome-info {
          background: white;
          border: 1px solid #dbe4ef;
          border-radius: 15px;
          padding: 12px;
        }

        .welcome-info label {
          display: block;
          color: #64748b;
          font-size: 10px;
          font-weight: 950;
          text-transform: uppercase;
        }

        .welcome-info b {
          display: block;
          margin-top: 5px;
          color: #071f45;
          font-size: 16px;
          font-weight: 950;
        }

        .welcome-actions {
          display: flex;
          justify-content: flex-end;
        }

        .welcome-btn {
          border: 0;
          background: #064e3b;
          color: white;
          border-radius: 13px;
          padding: 12px 18px;
          font-weight: 950;
          cursor: pointer;
          box-shadow: 0 12px 28px rgba(6,78,59,.22);
        }

        .welcome-btn:hover {
          background: #047857;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes popUp {
          from {
            opacity: 0;
            transform: translateY(14px) scale(.97);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @media(max-width:1100px) {
          .welcome-row {
            flex-direction: column;
            align-items: stretch;
          }

          .stats-card {
            min-width: auto;
          }

          .header-right {
            flex-wrap: wrap;
          }

          .module-grid {
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          }

          .workflow-command-grid {
            grid-template-columns: repeat(2,1fr);
          }

          .workflow-command-grid button:nth-child(2) {
            border-right: 0;
          }

          .workflow-command-grid button:nth-child(-n+2) {
            border-bottom: 1px solid #dbe4ec;
          }
        }

        @media(max-width:760px) {
          .pmc-wrap {
            width: calc(100% - 24px);
          }

          .pmc-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .brand {
            align-items: flex-start;
          }

          .brand h1 {
            white-space: normal;
            font-size: 22px;
          }

          .brand p {
            white-space: normal;
          }

          .nafed-logo {
            width: 138px;
            height: 74px;
          }

          .header-right {
            width: 100%;
            flex-wrap: wrap;
          }

          .stats-card {
            grid-template-columns: 1fr;
          }

          .stat {
            border-right: 0;
            border-bottom: 1px solid #e2e8f0;
          }

          .stat:last-child {
            border-bottom: 0;
          }

          .module-card {
            grid-template-columns: 54px 1fr 30px;
          }

          .module-icon {
            width: 52px;
            height: 52px;
          }

          .welcome-grid {
            grid-template-columns: 1fr;
          }

          .workflow-command-title {
            align-items: flex-start;
            flex-direction: column;
          }

          .workflow-command-grid {
            grid-template-columns: 1fr;
          }

          .workflow-command-grid button,
          .workflow-command-grid button:nth-child(2) {
            border-right: 0;
            border-bottom: 1px solid #dbe4ec;
          }

          .workflow-command-grid button:last-child {
            border-bottom: 0;
          }

          .module-head {
            flex-direction: column;
            align-items: flex-start;
          }
          .registration-head{align-items:flex-start;flex-direction:column}.registration-summary{grid-template-columns:1fr}.registration-card{grid-template-columns:1fr}.registration-data{grid-template-columns:repeat(2,1fr)}
        }
      `}</style>
      <div className="pmc-page">
        {welcomeOpen && tab === "home" && (
          <div className="welcome-pop">
            <div className="welcome-card">
              <div className="welcome-logo">
                <img src="/images/nafed-logo.png" alt="NAFED" />
              </div>

              <h2>Welcome, {userProfile?.name || "PMC User"}</h2>

              <p>
                NAFED PMC Portal me aapka swagat hai. Yahan sirf aapke allocated
                State/District ke NAFED Apply Form projects dikhai denge.
              </p>

              <div className="welcome-grid">
                <div className="welcome-info">
                  <label>State</label>
                  <b>{allocation?.state || "-"}</b>
                </div>

                <div className="welcome-info">
                  <label>Districts</label>
                  <b>{summary.districts}</b>
                </div>

                <div className="welcome-info">
                  <label>Projects</label>
                  <b>{summary.projects}</b>
                </div>
              </div>

              <div className="welcome-actions">
                <button
                  className="welcome-btn"
                  onClick={() => setWelcomeOpen(false)}
                >
                  Enter Dashboard
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="pmc-wrap">
          <header className="pmc-header">
            <div className="brand">
              <div className="nafed-logo">
                <img
                  src="/images/nafed-logo.png"
                  alt="NAFED Logo"
                  className="nafed-logo-img"
                />
              </div>

              <div>
                <h1>NAFED PMC Portal</h1>
                <p>Allocated PACS Project Management System</p>
              </div>
            </div>

            <div className="header-right">
              <div className="head-info">
                <span className="hicon">▦</span>
                <div>
                  <b>PMC Agency</b>
                  {allocation?.pmcName ||
                    userProfile?.organizationName ||
                    "PMC Agency"}
                </div>
              </div>

              <div className="head-info">
                <span className="hicon">⌖</span>
                <div>
                  <b>State</b>
                  {allocation?.state || "-"}
                </div>
              </div>

              <div
                className="live-sync"
                title={
                  liveSync.updatedAt
                    ? `Last synced ${liveSync.updatedAt.toLocaleTimeString("en-IN")}`
                    : "Connecting to Firestore"
                }
              >
                <span className="live-dot" />
                {liveSync.connected ? "LIVE DATA" : "CONNECTING"}
              </div>

              <div className="avatar">PM</div>

              <button className="logout" onClick={doLogout}>
                Logout
              </button>
            </div>
          </header>
          <main className="pmc-body">
            {tab === "home" ? (
              <>
                <section className="welcome-row">
                  <div className="welcome">
                    <h2>Welcome, {userProfile?.name || "PMC User"}</h2>
                    <p>
                      Manage and monitor your allocated PACS projects
                      professionally.
                    </p>
                  </div>

                  <div className="stats-card">
                    <div className="stat">
                      <div
                        className="stat-icon"
                        style={{ background: "#dcfce7", color: "#15803d" }}
                      >
                        01
                      </div>
                      <div>
                        <label>Allocated Districts</label>
                        <b>{summary.districts}</b>
                      </div>
                    </div>

                    <div className="stat">
                      <div
                        className="stat-icon"
                        style={{ background: "#dbeafe", color: "#2563eb" }}
                      >
                        02
                      </div>
                      <div>
                        <label>Assigned Projects</label>
                        <b>{summary.projects}</b>
                      </div>
                    </div>

                    <div className="stat">
                      <div
                        className="stat-icon"
                        style={{ background: "#f3e8ff", color: "#9333ea" }}
                      >
                        03
                      </div>
                      <div>
                        <label>PACS Applications</label>
                        <b>{summary.pacs}</b>
                      </div>
                    </div>

                    <div className="stat">
                      <div
                        className="stat-icon"
                        style={{ background: "#ffedd5", color: "#ea580c" }}
                      >
                        04
                      </div>
                      <div>
                        <label>Total Capacity</label>
                        <b>{formatCapacity(summary.capacity)}</b>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="workflow-command">
                  <div className="workflow-command-title">
                    <div>
                      <small>OFFICIAL PROJECT CONTROL</small>
                      <h3>PMC Workflow Command Centre</h3>
                      <p>
                        Live completion status across document verification,
                        MOU, DPR, Assurance Letter and Registrar forwarding.
                      </p>
                    </div>
                    <span>{summary.projects} ALLOCATED PROJECTS</span>
                  </div>
                  <div className="workflow-command-grid">
                    {[
                      ["01", "Documents Approved", workflowSummary.documents, "pacsDocumentScrutiny"],
                      ["02", "MOU Fully Executed", workflowSummary.mou, "mouExecution"],
                      ["03", "DPR Published", workflowSummary.dpr, "dprGenerator"],
                      ["04", "Assurance Published", workflowSummary.assurance, "assuranceLetter"],
                      ["05", "Forwarded to District", workflowSummary.registrar, "registrarForwarding"],
                    ].map(([number, label, value, target]) => (
                      <button type="button" key={target} onClick={() => setTab(target)}>
                        <span>{number}</span>
                        <div><small>{label}</small><b>{value} / {summary.projects}</b></div>
                        <em>Open ›</em>
                      </button>
                    ))}
                  </div>
                </section>

                <section className="home-card">
                  <div className="section-title">
                    <h3>PMC Operations & Document Workflow</h3>
                    <p>
                      Select an authorised module to manage allocated PACS projects.
                    </p>
                  </div>

                  <div className="module-grid">
                    {MODULES.map((m) => (
                      <button
                        key={m.key}
                        type="button"
                        className="module-card"
                        onClick={() => setTab(m.key)}
                      >
                        <div className={`module-icon ${m.color}`}>{m.icon}</div>

                        <div style={{ textAlign: "left" }}>
                          <small className="module-label">AUTHORISED PMC MODULE</small>
                          <h4>{m.title}</h4>
                          <p>{m.desc}</p>
                        </div>

                        <div className="arrow">›</div>
                      </button>
                    ))}
                  </div>
                </section>
              </>
            ) : (
              <section className="module-page">
                <div className="module-head">
                  <div>
                    <h2>{activeModule?.title || "Module"}</h2>
                    <p>{activeModule?.desc || "NAFED PMC Module"}</p>
                  </div>

                  <button className="back-btn" onClick={() => setTab("home")}>
                    ← Back to PMC Home
                  </button>
                </div>

                <div className="module-content">{renderModule()}</div>
              </section>
            )}
          </main>
        </div>
      </div>
    </>
  );
}
